import { initialLocale, rememberLocaleChoice } from "./lib/locale-preference";
import { useDashboardScroll } from "./lib/use-dashboard-scroll";
import { fetchProviderSignupCountries, fetchProviderSignupCountryHint } from "./lib/provider-signup-countries";
import { ProviderSignupCountryPicker } from "./components/ProviderSignupCountryPicker";
import type { Session } from "@supabase/supabase-js";
import { isIsoCountryCode } from "@tinkr/shared/countries";
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  BookOpen,
  ChevronDown,
  FlaskConical,
  Gauge,
  KeyRound,
  Languages,
  Layers,
  LifeBuoy,
  LockKeyhole,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Moon,
  Package,
  Receipt,
  RefreshCcw,
  ShieldCheck,
  Ship,
  Sun,
  Terminal,
  UserCog,
  Wallet,
  Webhook,
  Wrench,
  X
} from "lucide-react";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { type FormEvent, Suspense, lazy, useCallback, useEffect, useMemo, useOptimistic, useRef, useState, useTransition } from "react";
import { createPortal } from "react-dom";

// ADR-WES1: console-only components load with ConsoleSurface (./console).
import {
  AccessDashboard,
  AdminNotificationsBell,
  DashboardHelpStrip,
  DashboardRefreshControls,
  FeedbackDialog,
  LegalAcceptanceGate,
  PartnerAnnouncementsBell,
  ProviderNotificationsBell,
  StitchErrorBoundary
} from "./console/lazy-console";
import { loadAdminShell } from "./console/load-console";
import { preloadConsoleForSignIn, preloadForAuthScreen } from "./lib/auth-screen-preload";
import { ConsoleNavProgress, ConsoleSkeleton } from "./components/ConsoleSkeleton";
import { consoleChromeForSession } from "./lib/console-chrome";
import { peekSettledConsolePrefetch, startConsoleBootstrap, takeConsolePrefetch } from "./lib/console-bootstrap";
import { prefetchProviderSection } from "./lib/console-loaders";
import {
  buildProviderNavGroups,
  DashboardRefreshButton,
  ProviderBottomTabBar,
  providerChip,
  providerNavHighlight,
  SkipToContentLink,
  ConsoleDocumentTitle,
  PendingOfferBadge,
  useDrawerFocusManagement
} from "./components/ProviderConsoleChrome";
import { useProviderConsoleState } from "./lib/provider-access-status";
import { providerNavigationStage } from "./lib/provider-display-state";
const AccountSettingsPanel = lazy(() => import("./components/AccountSettingsPanel").then(module => ({ default: module.AccountSettingsPanel })));
const AdminShell = lazy(loadAdminShell);
import {
  ADMIN_LEGACY_SECTION_IDS,
  normalizeAdminUrl,
  resolveAdminLocation
} from "./components/admin/adminLocation";
import { isAdminSection } from "./components/admin/adminSections";
import type { PartnerLifecycleDraftState } from "./components/PartnerLifecyclePanel";
import { renderLegalConsentText } from "./components/legal-consent-text";
const PartnerApiInstructionsPanel = lazy(() => import("./components/PartnerApiInstructionsPanel").then(module => ({ default: module.PartnerApiInstructionsPanel })));
const PartnerApiKeysPanel = lazy(() => import("./components/PartnerApiKeysPanel").then(module => ({ default: module.PartnerApiKeysPanel })));
const PartnerApplicationForm = lazy(() => import("./components/PartnerApplicationForm").then(module => ({ default: module.PartnerApplicationForm })));
const PartnerOnboardingPanel = lazy(() => import("./components/PartnerOnboardingPanel").then(module => ({ default: module.PartnerOnboardingPanel })));
const PartnerMatchingPreferencesPanel = lazy(() => import("./components/PartnerMatchingPreferencesPanel").then(module => ({ default: module.PartnerMatchingPreferencesPanel })));
const PartnerAppCoveragePanel = lazy(() => import("./components/PartnerAppCoveragePanel"));
const PartnerNotificationPreferencesPanel = lazy(() => import("./components/PartnerNotificationPreferencesPanel").then(module => ({ default: module.PartnerNotificationPreferencesPanel })));
const PartnerOrdersPanel = lazy(() => import("./components/PartnerOrdersPanel").then(module => ({ default: module.PartnerOrdersPanel })));
const PartnerOverviewPanel = lazy(() => import("./components/PartnerOverviewPanel").then(module => ({ default: module.PartnerOverviewPanel })));
const PartnerPayoutsPanel = lazy(() => import("./components/PartnerPayoutsPanel").then(module => ({ default: module.PartnerPayoutsPanel })));
const PartnerShippingCoveragePanel = lazy(() => import("./components/PartnerShippingCoveragePanel").then(module => ({ default: module.PartnerShippingCoveragePanel })));
const PartnerWebhooksPanel = lazy(() => import("./components/PartnerWebhooksPanel").then(module => ({ default: module.PartnerWebhooksPanel })));
const SandboxPlaygroundPanel = lazy(() => import("./components/SandboxPlaygroundPanel").then(module => ({ default: module.SandboxPlaygroundPanel })));
const TeaserSplash = lazy(() => import("./components/TeaserSplash").then(module => ({ default: module.TeaserSplash })));
import { ConsentBanner, ConsentSettingsLink, StandaloneConsentSettings } from "./components/ConsentBanner";
import { docsNavLabel } from "./docs/navLabel";
import { QuickPrintNavLink, quickPrintPage } from "./quick-print-entry";
import { SplashNavCta, SplashSkipLink } from "./components/splash/SplashChrome";
import { track } from "./lib/analytics";
import { applySeo } from "./lib/seo";
import { sameSessionIdentity, setCurrentApiSession } from "./lib/api-session";
import { navigateDashboardUrl, clearDashboardLocations, rememberDashboardLocation, restoreDashboardLocation } from "./lib/dashboard-location";
import { reconcileAccountDeviceState } from "./lib/account-device-state";
import {
  accountRoleForSession,
  resolveAccountRole,
  type AccountRole
} from "./lib/account-role";
import { isProductionHost } from "./lib/environment";
import { isLegalSignupBlocked } from "./lib/legal-publication";
import { describeApiErrorWithFallback } from "./lib/api-error-message";
import {
  getPartnerSelfOverview,
  listOwnPartnerApplications,
  type PartnerSelfOverview
} from "./lib/tinkr-api/partner-self";
import { useMobileEdgeSwipeDrawer } from "./lib/use-mobile-edge-swipe-drawer";
import { DashboardRefreshProvider, useDashboardRefresh } from "./lib/dashboard-refresh";
import {
  resetDashboardResourceCache,
  useDashboardResource
} from "./lib/dashboard-resource";
import {
  acknowledgeProviderAnnouncement,
  createPasswordReauth,
  getPlatformStatus,
  listProviderAnnouncements,
  setTinkrApiReauthHandler,
  TinkrApiError,
  type OperationalAnnouncement,
  type PlatformStatusResponse
} from "./lib/tinkr-api";
import {
  changeProviderPassword,
  consumeAuthCallback,
  getCurrentSession,
  hasSupabaseConfig,
  requestProviderPasswordReset,
  resendProviderVerification,
  signInProviderWithPassword,
  signOut,
  signUpProviderWithPassword,
  subscribeToAuthChanges,
  updateProviderPassword
} from "./lib/supabase";
import { BRAND } from "./lib/brand";
import {
  loadLocale,
  localeMeta,
  supportedLocales,
  translate,
  type Translate,
  type SupportedLocale
} from "./i18n";
import { runLatestLocaleRequest } from "./lib/latest-locale-request";
import { useMessagesVersion } from "./lib/use-messages-version";
import { authModeFromUrl, bootPlan, screenWaitsForAuth, urlRequiresAuthCheck } from "./boot/first-screen";
import { ConsoleSurface, DocsApp, SplashScreen } from "./boot/screens";
import { hasStoredSession } from "./boot/stored-session";
import { useThemePreference } from "./lib/use-theme-preference";

type Theme = "dark" | "light";
type Screen = "splash" | "auth" | "dashboard" | "api" | "docs";
type AuthMode = "login" | "signup" | "forgot" | "reset" | "invite";
type PasswordSetupMode = Extract<AuthMode, "reset" | "invite">;
type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "unverified" | "error";
type AuthState = {
  status: AuthStatus;
  session: Session | null;
  error?: string;
};
type AdminReauthChallenge = {
  resolve: (session: Session | null) => void;
  reject: () => void;
};
type AuthErrorLike = Error & {
  code?: unknown;
  status?: unknown;
};

function sessionEmailVerified(session: Session): boolean {
  return Boolean(session.user.email_confirmed_at || session.user.confirmed_at);
}

function sessionHasPasswordCredential(session: Session | null): boolean {
  if (!session) return false;

  const appProvider =
    typeof session.user.app_metadata?.provider === "string"
      ? session.user.app_metadata.provider
      : null;
  const appProviders = Array.isArray(session.user.app_metadata?.providers)
    ? session.user.app_metadata.providers.filter(
        (provider): provider is string => typeof provider === "string"
      )
    : [];
  const identityProviders = Array.isArray(session.user.identities)
    ? session.user.identities
        .map((identity) => identity.provider)
        .filter((provider): provider is string => typeof provider === "string")
    : [];
  const knownProviders = [appProvider, ...appProviders, ...identityProviders].filter(
    (provider): provider is string => Boolean(provider)
  );

  // Older password sessions may not include provider metadata. Treat unknown
  // as password-capable, but hide the control once OAuth-only identities are
  // explicit.
  if (knownProviders.length === 0) return true;
  return knownProviders.includes("email");
}

function authStateFromSession(session: Session | null): AuthState {
  if (!session) return { status: "unauthenticated", session: null };
  if (!sessionEmailVerified(session)) {
    return { status: "unverified", session };
  }
  return { status: "authenticated", session };
}

function humanReadableAuthError(caught: unknown, t: Translate): string {
  const errorLike =
    caught instanceof Error || (typeof caught === "object" && caught !== null)
      ? (caught as AuthErrorLike)
      : null;
  const code = typeof errorLike?.code === "string" ? errorLike.code : "";
  const status = typeof errorLike?.status === "number" ? errorLike.status : null;

  if (code === "invalid_credentials") {
    return t("auth.invalidCredentials");
  }
  if (status === 429 || (code.startsWith("over_") && code.endsWith("_rate_limit"))) {
    return t("auth.rateLimited");
  }
  if (code === "weak_password" || code === "password_too_short") {
    return t("auth.passwordServerRequirement");
  }

  const raw = caught instanceof Error ? caught.message : "";
  if (raw.includes("provider_signup_country_not_supported")) return t("auth.countryUnavailable");
  if (raw.includes("provider_signup_country_check_failed")) return t("auth.countryCheckFailed");
  const message = raw
    .replace(/^(AuthApiError|Error):\s*/i, "")
    .replace(/\s*\[(?:status|code)[^\]]+\]\s*$/i, "")
    .trim();

  if (!message) return t("auth.errorFallback");
  if (/invalid login credentials/i.test(message)) {
    return t("auth.invalidCredentials");
  }
  if (/rate limit|too many requests|over email send rate limit/i.test(message)) {
    return t("auth.rateLimited");
  }
  if (
    /password/i.test(message) &&
    /(weak|requirement|should|must|characters|length|short|strength|complexity)/i.test(message)
  ) {
    return t("auth.passwordServerRequirement");
  }
  return t("auth.errorFallback");
}

function parseAuthModeFromLocation(): AuthMode | null {
  if (typeof window === "undefined") return null;
  // `authMode` is kept as a legacy alias for links generated during the
  // pre-Phase-1 auth spike. URL sync below rewrites to canonical `mode`.
  // Shared with the head script's first-screen decision (ADR-WBC1).
  return authModeFromUrl(window.location);
}

/** The current history entry's state, when it is an object, to extend on replace. */
function historyStateObject(): Record<string, unknown> {
  const state: unknown = window.history.state;
  return state && typeof state === "object" ? (state as Record<string, unknown>) : {};
}

function isPasswordSetupMode(authMode: AuthMode): boolean {
  return authMode === "reset" || authMode === "invite";
}

function passwordSetupMode(authMode: AuthMode | null): PasswordSetupMode | null {
  return authMode === "reset" || authMode === "invite" ? authMode : null;
}

export function App() {
  const quickPrint = typeof window !== "undefined" ? quickPrintPage(window.location.pathname) : null;
  if (quickPrint) return quickPrint;
  return <Suspense fallback={<div className="app-boot" role="status">Tinkr…</div>}><AppContent /></Suspense>;
}

function AppContent() {
  useEffect(() => {
    const navigate = (event: MessageEvent) => {
      if (event.data?.type !== "tinkr:navigate" || typeof event.data.url !== "string") return;
      if (navigateDashboardUrl(event.data.url)) event.ports?.[0]?.postMessage({ type: "tinkr:navigated" });
    };
    navigator.serviceWorker?.addEventListener("message", navigate);
    return () => navigator.serviceWorker?.removeEventListener("message", navigate);
  }, []);
  // Pre-launch teaser kill-switch. Set `VITE_TEASER_MODE=true` in Cloudflare
  // Pages → Settings → Environment Variables per environment and redeploy to
  // replace the entire app surface with the teaser splash. Any other value
  // (including unset) renders the normal app. See `apps/web/README.md`.
  if (import.meta.env.VITE_TEASER_MODE === "true") {
    return <TeaserSplash />;
  }

  // ADR-WBC1: the head script (src/boot/head.ts) sets <html data-theme>
  // before the first paint from the same source this hook starts from; the
  // theme follows the OS until the visitor picks one with a toggle, and only
  // that explicit choice is stored.
  const [theme, setTheme] = useThemePreference();
  // The first screen: what the URL asks for, except that a visitor whose
  // browser holds a session goes from the site root straight to the console.
  // The hint never authorizes anything (src/boot/first-screen.ts).
  const [initialBoot] = useState(() => {
    const location = typeof window === "undefined" ? { pathname: "/", search: "", hash: "" } : window.location;
    const sessionHint = typeof window !== "undefined" && hasStoredSession(import.meta.env);
    return { ...bootPlan(location, sessionHint), sessionHint, authCallback: urlRequiresAuthCheck(location) };
  });
  const [screen, setScreen] = useState<Screen>(initialBoot.screen);
  // Screen changes the app makes on its own (auth redirects, the signed-in
  // landing) replace the history entry instead of adding one, so Back never
  // lands on a page that immediately redirects again (ADR-WBC1, S-15).
  const redirectNextScreen = useRef(false);
  const currentScreen = useRef(screen);
  currentScreen.current = screen;
  const redirectScreen = useCallback((next: Screen) => {
    if (currentScreen.current !== next) redirectNextScreen.current = true;
    setScreen(next);
  }, []);
  // The console was chosen by the stored-session hint alone; if the auth check
  // finds no session after all, the visitor gets the splash they asked for.
  const [sessionHintLanding, setSessionHintLanding] = useState(initialBoot.promotedBySession);
  const [authMode, setAuthMode] = useState<AuthMode>(
    () => parseAuthModeFromLocation() ?? "signup"
  );
  const [requiredPasswordSetupMode, setRequiredPasswordSetupMode] =
    useState<PasswordSetupMode | null>(null);
  const [authState, setAuthState] = useState<AuthState>({
    status: "loading",
    session: null
  });
  const [locale, setLocale] = useState<SupportedLocale>(initialLocale);
  // Non-English locales load on demand. Keep the current language visible
  // until the requested dictionary is ready, then force a translated repaint.
  const [localeLoadEpoch, setLocaleLoadEpoch] = useState(0);
  const [localeLoading, setLocaleLoading] = useState(false);
  const [localeLoadError, setLocaleLoadError] = useState<SupportedLocale | null>(null);
  const localeRequestGeneration = useRef(0);
  const messagesVersion = useMessagesVersion();
  const t = useMemo<Translate>(
    () => (key) => translate(locale, key),
    [locale, localeLoadEpoch, messagesVersion]
  );
  const visibleAuthMode = requiredPasswordSetupMode ?? authMode;
  // Locale switches by the USER go through this wrapper so the analytics
  // event fires exactly on the action, not on bootstrap detection.
  const changeLocale = useCallback((next: SupportedLocale) => {
    setLocaleLoading(true);
    setLocaleLoadError(null);
    void runLatestLocaleRequest({
      generation: localeRequestGeneration,
      locale: next,
      load: loadLocale,
      onSuccess: () => {
        track.localeChanged(next);
        rememberLocaleChoice(next);
        setLocale(next);
        // A retry of the selected locale must repaint after its chunk arrives.
        setLocaleLoadEpoch((epoch) => epoch + 1);
        setLocaleLoading(false);
      },
      onError: (_locale, error) => {
        console.error("Failed to load locale chunk", { locale: next, error });
        setLocaleLoadError(next);
        setLocaleLoading(false);
      }
    });
  }, []);
  const setAuthModeFromUi = useCallback(
    (mode: AuthMode) => {
      setAuthMode(mode);
      const setupMode = passwordSetupMode(mode);
      if (setupMode && authState.status === "authenticated") {
        setRequiredPasswordSetupMode(setupMode);
      }
    },
    [authState.status]
  );

  useEffect(() => {
    let cancelled = false;
    void loadLocale(locale).then(
      () => {
        if (!cancelled) {
          setLocaleLoadError(null);
          setLocaleLoadEpoch((epoch) => epoch + 1);
        }
      },
      (error) => {
        if (!cancelled) {
          console.error("Failed to load locale chunk", { locale, error });
          setLocaleLoadError(locale);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    const { dir } = localeMeta[locale];
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale]);

  // Keep the document head (title, description, robots, canonical,
  // hreflang, og:*) in sync with the visible screen and locale. `t` is
  // memoized on [locale, localeLoadEpoch], so this re-runs once the
  // locale chunk finishes loading and localized strings become available.
  useEffect(() => {
    applySeo({ screen, locale, t, hostname: window.location.hostname });
  }, [screen, locale, t]);

  // Analytics page views ride the same screen transitions applySeo does.
  // This is a state-machine SPA, so the screen id (not a URL) is the page.
  useEffect(() => {
    track.pageView(screen);
  }, [screen]);

  // The signup-mode auth screen opening is the provider-acquisition funnel's
  // entry milestone, however the visitor got there (splash CTA, nav, deep link).
  useEffect(() => {
    if (screen === "auth" && visibleAuthMode === "signup") {
      track.providerSignupStarted();
    }
  }, [screen, visibleAuthMode]);

  const [signedOutToast, setSignedOutToast] = useState(false);
  const authT = useRef(t);
  authT.current = t;
  const sessionOwner = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    function applySession(next: Session | null) {
      if (sessionOwner.current && sessionOwner.current !== next?.user.id) clearDashboardLocations();
      sessionOwner.current = next?.user.id ?? null;
      setCurrentApiSession(next);
      resetDashboardResourceCache(next?.user.id ?? null);
      reconcileAccountDeviceState(next?.user.id ?? null);
      setAuthState((previous) =>
        previous.status !== "loading" && previous.status !== "error" && sameSessionIdentity(previous.session, next)
          ? previous : authStateFromSession(next));
    }

    void consumeAuthCallback()
      .then((callbackSession) => callbackSession ?? getCurrentSession())
      .then((current) => {
        if (cancelled) return;
        const setupMode = passwordSetupMode(parseAuthModeFromLocation());
        setRequiredPasswordSetupMode(current && setupMode ? setupMode : null);
        applySession(current);
      })
      .catch((error) => {
        if (cancelled) return;
        setAuthState({
          status: "error",
          session: null,
          error: humanReadableAuthError(error, authT.current)
        });
      });

    const unsubscribe = subscribeToAuthChanges((event, nextSession) => {
      if (event === "PASSWORD_RECOVERY") {
        setRequiredPasswordSetupMode(nextSession ? "reset" : null);
        setAuthMode("reset");
        setScreen("auth");
        applySession(nextSession);
        return;
      }
      // SIGNED_OUT can fire from this tab (user clicked Sign out) OR from
      // another tab/window (supabase localStorage broadcast). We want to
      // detect the "expired session" case and bounce out of the dashboard
      // cleanly without losing the user's view selection in the URL.
      if (event === "SIGNED_OUT") {
        setRequiredPasswordSetupMode(null);
        applySession(null);
        clearDashboardLocations();
        setScreen("splash");
        setSignedOutToast(true);
        return;
      }
      applySession(nextSession);
      if (nextSession) setSignedOutToast(false);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // A signed-in visitor who opens the site root (no ?screen=) lands on their
  // dashboard instead of the marketing splash (founder, 2026-09-30). Runs
  // once, on the first resolved auth state; later navigation to the splash
  // (logo click) is deliberate and is left alone.
  // With a stored session the console is already the first screen
  // (src/boot/first-screen.ts); this covers sessions the hint cannot see.
  const initialLandingResolved = useRef(false);
  useEffect(() => {
    if (initialLandingResolved.current || authState.status === "loading") return;
    initialLandingResolved.current = true;
    if (typeof window === "undefined") return;
    if (sessionHintLanding) {
      setSessionHintLanding(false);
      if (authState.status === "unauthenticated" || authState.status === "error") {
        redirectScreen("splash");
        return;
      }
    }
    if (authState.status !== "authenticated" || requiredPasswordSetupMode) return;
    if (screen !== "splash") return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("screen") || params.has("dashboard")) return;
    redirectScreen("dashboard");
  }, [authState.status, redirectScreen, requiredPasswordSetupMode, screen, sessionHintLanding]);

  // Public surfaces (splash nav, hero CTA, auth screen) open the dashboard
  // at its first section rather than restoring the last visited console
  // page: "Return to dashboard" should mean the dashboard, not wherever the
  // visitor was before clicking the logo (mobile feedback, 2026-09-30).
  const openPublicScreen = useCallback((next: Screen) => {
    if (next === "dashboard" && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      params.delete("section");
      params.delete("subtab");
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
      );
      clearDashboardLocations();
    }
    setScreen(next);
  }, []);

  useEffect(() => {
    if (authState.status === "loading") return;
    if (
      requiredPasswordSetupMode &&
      authState.status === "authenticated" &&
      screen !== "auth"
    ) {
      redirectScreen("auth");
      return;
    }
    if (screen === "dashboard" && authState.status === "unauthenticated" && !sessionHintLanding) {
      setAuthMode("login");
      redirectScreen("auth");
      return;
    }
    if (
      screen === "auth" &&
      authState.status === "authenticated" &&
      !requiredPasswordSetupMode &&
      !isPasswordSetupMode(authMode)
    ) {
      redirectScreen("dashboard");
    }
  }, [authMode, authState.status, redirectScreen, requiredPasswordSetupMode, screen, sessionHintLanding]);

  const session = authState.session;
  // ADR-WBC1 (S-01): public screens render at once; the console, auth
  // callbacks and password setup wait for the auth check (first-screen.ts).
  const waitsForAuth = screenWaitsForAuth(screen, initialBoot);
  const resolvedScreen: Screen | "loading" | "verify-email" =
    authState.status === "loading"
      ? waitsForAuth ? "loading" : screen
      : sessionHintLanding && screen === "dashboard" && authState.status !== "authenticated" && authState.status !== "unverified"
        ? "splash"
      : authState.status === "unverified"
        ? "verify-email"
        : requiredPasswordSetupMode && authState.status === "authenticated"
          ? "auth"
        : screen === "dashboard" && authState.status !== "authenticated"
          ? "auth"
          : screen === "auth" &&
              authState.status === "authenticated" &&
              !requiredPasswordSetupMode &&
              !isPasswordSetupMode(authMode)
            ? "dashboard"
            : screen;

  // P-01: the moment a session is known for the dashboard, start the console
  // chunk, legal status, provider access, platform status, the section chunk
  // and (for a known provider) Home's reads together. Rendering still waits
  // for the legal gate exactly as before.
  useEffect(() => {
    if (resolvedScreen === "dashboard" && session) startConsoleBootstrap(session, locale);
  }, [resolvedScreen, session, locale]);

  // Keep ?screen= in the URL so back-button works and deep links survive
  // refresh. pushState (not replaceState) so navigation history exists, but
  // the landing sync and the app's own redirects replace the current entry:
  // a new tab opened on the site root has one entry, not two (S-15).
  const screenUrlSynced = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const replace = !screenUrlSynced.current || redirectNextScreen.current;
    screenUrlSynced.current = true;
    redirectNextScreen.current = false;
    const params = new URLSearchParams(window.location.search);
    const current = params.get("screen");
    // Under /docs the path already names the docs screen: no ?screen=docs.
    const docsPath = screen === "docs" && window.location.pathname.startsWith("/docs");
    if (current === screen && !docsPath) return;
    if (screen === "splash") {
      params.delete("screen");
      params.delete("dashboard");
    } else if (docsPath) {
      params.delete("screen");
    } else {
      params.set("screen", screen);
    }
    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    if (docsPath) {
      if (current !== null) window.history.replaceState(window.history.state, "", next);
      return;
    }
    if (next === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;
    if (replace) window.history.replaceState({ ...historyStateObject(), screen }, "", next);
    else window.history.pushState({ screen }, "", next);
  }, [screen]);

  // Take over scroll restoration once so the browser does not re-apply a
  // saved scroll position on back/forward and fight the per-transition reset
  // below.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Reset scroll to the top whenever the public screen (or the visible auth
  // mode) changes. The app is a state-machine SPA with no router, so nothing
  // else resets window scroll on a transition. CTAs like "Become a provider"
  // appear in both the splash hero and the bottom final-CTA band; without this
  // the destination screen would inherit the scroll offset of the band the
  // user clicked from and land scrolled near the bottom. The docs portal keeps
  // its own sub-anchor scrollIntoView in DocsApp.tsx; this only governs
  // top-level screen swaps.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo(0, 0);
  }, [screen, visibleAuthMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const previous = params.toString();

    if (screen === "auth") {
      params.set("mode", visibleAuthMode);
      params.delete("authMode");
    } else {
      params.delete("mode");
      params.delete("authMode");
    }

    if (params.toString() === previous) {
      return;
    }

    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", next);
  }, [screen, visibleAuthMode]);

  // popstate so the browser back button restores the previous screen.
  // Path-aware: anything under /docs stays on the docs portal (so clicking
  // back across docs sections doesn't drop the user to splash). Otherwise
  // we read the ?screen= query param.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onPop() {
      if (window.location.pathname.startsWith("/docs")) {
        setScreen("docs");
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const requested = params.get("screen");
      if (requested === "dashboard" || requested === "api" || requested === "auth" || requested === "docs") {
        setScreen(requested);
        if (requested === "auth") {
          setAuthMode(parseAuthModeFromLocation() ?? "signup");
        }
      } else if (params.get("dashboard") === "1") {
        setScreen("dashboard");
      } else {
        setScreen("splash");
      }
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <main
      className="app-shell"
      data-dashboard={resolvedScreen === "dashboard" ? "provider" : undefined}
      data-public={resolvedScreen !== "dashboard"}
      data-theme={theme}
    >
      {localeLoading ? (
        <p className="locale-load-notice" role="status">{t("locale.loading")}</p>
      ) : localeLoadError ? (
        <p className="locale-load-notice" role="alert">
          <span>{t("locale.loadError")}</span>{" "}
          <button className="button-link" onClick={() => changeLocale(localeLoadError)} type="button">
            {t("locale.retry")}
          </button>
        </p>
      ) : null}
      {resolvedScreen === "loading" ? (
        screen === "dashboard"
          ? <ConsoleSkeleton label={t("auth.resolvingSession")} variant="full" />
          : <AuthBootstrapScreen t={t} />
      ) : resolvedScreen === "verify-email" && session ? (
        <VerifyEmailScreen
          locale={locale}
          onLogout={() => void signOut()}
          session={session}
          setLocale={changeLocale}
          setScreen={setScreen}
          setTheme={setTheme}
          t={t}
          theme={theme}
        />
      ) : resolvedScreen === "dashboard" && session ? (
        // P-03: the console chunk suspends here with a console-shaped
        // skeleton instead of the app-wide "Tinkr…" boot screen.
        <Suspense fallback={<ConsoleSkeleton chrome={consoleChromeForSession(session)} label={t("access.dashboardLoading")} testId="console-surface-loading" variant="full" />}>
        <ConsoleSurface><LegalAcceptanceGate
          locale={locale}
          onSignOut={() => void signOut()}
          session={session}
          t={t}
        >
          <OperationsDashboard
            key={session.user.id}
            session={session}
            locale={locale}
            setScreen={setScreen}
            setLocale={changeLocale}
            setTheme={setTheme}
            t={t}
            theme={theme}
          />
        </LegalAcceptanceGate></ConsoleSurface>
        </Suspense>
      ) : resolvedScreen === "dashboard" ? (
        <ConsoleSkeleton label={t("auth.resolvingSession")} variant="full" />
      ) : resolvedScreen === "docs" ? (
        <ConsoleSurface><DocsApp
          locale={locale}
          session={session}
          sessionPending={authState.status === "loading"}
          setScreen={setScreen}
          setTheme={setTheme}
          theme={theme}
        /></ConsoleSurface>
      ) : (
        <>
          <PublicShell
            authMode={visibleAuthMode}
            authStatus={authState.status}
            locale={locale}
            onPasswordSetupComplete={() => {
              setRequiredPasswordSetupMode(null);
              setAuthState({ status: "unauthenticated", session: null });
              setSignedOutToast(false);
            }}
            session={session}
            screen={
              resolvedScreen === "api" ||
              resolvedScreen === "auth" ||
              resolvedScreen === "splash"
                ? resolvedScreen
                : "splash"
            }
            setScreen={openPublicScreen}
            setAuthMode={setAuthModeFromUi}
            setLocale={changeLocale}
            setTheme={setTheme}
            t={t}
            theme={theme}
          />
          {signedOutToast ? (
            <div className="stitch-shell" data-toast-only="1">
              <div className="stitch-signed-out-toast" role="status">
                <div>
                  <strong>{t("stitch.signedOut.title")}</strong>
                  <span>{t("stitch.signedOut.body")}</span>
                </div>
                <button
                  className="stitch-icon-button"
                  onClick={() => setSignedOutToast(false)}
                  type="button"
                  aria-label={t("common.dismiss")}
                >
                  ×
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
      <ConsentBanner t={t} />
    </main>
  );
}

function AuthBootstrapScreen({ t }: { t: Translate }) {
  return (
    <section
      aria-busy="true"
      className="auth-bootstrap-screen"
      data-testid="auth-bootstrap-screen"
      role="status"
    >
      <div className="auth-bootstrap-card">
        <BrandLogo />
        <div className="auth-bootstrap-lines" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <p>{t("auth.resolvingSession")}</p>
      </div>
    </section>
  );
}

function VerifyEmailScreen({
  locale,
  onLogout,
  session,
  setLocale,
  setScreen,
  setTheme,
  t,
  theme
}: {
  locale: SupportedLocale;
  onLogout: () => void;
  session: Session;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  setTheme: (theme: Theme) => void;
  t: Translate;
  theme: Theme;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const email = session.user.email ?? "";

  async function resend() {
    if (!email) return;
    setSubmitting(true);
    setStatus(null);
    setError(null);
    try {
      await resendProviderVerification(email);
      setStatus(t("auth.verificationResent"));
    } catch (caught) {
      setError(humanReadableAuthError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="public-shell is-warm-auth">
      <section className="warm-auth" data-testid="verify-email-gate">
        <aside className="warm-auth-panel">
          <button
            aria-label={BRAND.name}
            className="warm-auth-logo link-reset"
            onClick={() => setScreen("splash")}
            type="button"
          >
            <img alt="" src={BRAND.logos.fullColorMark} />
          </button>
          <div className="warm-auth-panel-copy">
            <span aria-hidden="true" className="warm-auth-diamonds">
              <i />
              <i />
              <i />
            </span>
            <p className="warm-auth-panel-title">{t("auth.verifyTitle")}</p>
            <p className="warm-auth-panel-body">{t("auth.verifyBody")}</p>
          </div>
          <p className="warm-auth-tagline">{t("auth.panelTagline")}</p>
        </aside>
        <div className="warm-auth-main">
          <div className="warm-auth-prefs">
            <LanguageSelect compact locale={locale} setLocale={setLocale} t={t} />
            <button
              className="icon-button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              title={theme === "dark" ? t("nav.themeLight") : t("nav.themeDark")}
              type="button"
            >
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>
          <div className="warm-auth-card">
            <h1>{t("auth.checkEmailTitle")}</h1>
            <p className="warm-auth-switch">{t("auth.checkEmailBody")}</p>
            {email ? <p className="warm-auth-email">{email}</p> : null}
            {status ? <p className="form-note good">{status}</p> : null}
            {error ? <p className="form-note error">{error}</p> : null}
            <button
              className="warm-auth-submit"
              disabled={submitting || !email}
              onClick={() => void resend()}
              type="button"
            >
              <span>{submitting ? t("auth.sending") : t("auth.resendVerification")}</span>
              <RefreshCcw size={17} />
            </button>
            <button className="warm-auth-secondary" onClick={onLogout} type="button">
              <span>{t("dashboard.signOut")}</span>
              <LogOut size={17} />
            </button>
          </div>
          {/* resolvedScreen forces this gate for every route while the session
              is unconfirmed, so it must carry its own Cookie choices control. */}
          <StandaloneConsentSettings t={t} />
        </div>
      </section>
    </section>
  );
}

function PublicShell({
  authMode,
  authStatus,
  locale,
  onPasswordSetupComplete,
  session,
  screen,
  setAuthMode,
  setLocale,
  setScreen,
  setTheme,
  t,
  theme
}: {
  authMode: AuthMode;
  authStatus: AuthStatus;
  locale: SupportedLocale;
  onPasswordSetupComplete: () => void;
  session: Session | null;
  screen: Screen;
  setAuthMode: (mode: AuthMode) => void;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  setTheme: (theme: Theme) => void;
  t: Translate;
  theme: Theme;
}) {
  // Chrome varies per screen (design "Tinkr Splash Flow" + "Tinkr Auth"):
  // the auth screen is a standalone split panel with NO public nav/footer
  // (the teal panel's logo links home; a discreet locale/theme row lives in
  // the form column); the splash keeps the nav but its footer is merged
  // into the teal closer band; the api screen keeps the classic chrome
  // until the partner surface gets its own redesign pass.
  if (screen === "auth") {
    return (
      <section className="public-shell is-warm-auth">
        <AuthScreen
          authMode={authMode}
          authStatus={authStatus}
          locale={locale}
          onPasswordSetupComplete={onPasswordSetupComplete}
          session={session}
          setAuthMode={setAuthMode}
          setLocale={setLocale}
          setScreen={setScreen}
          setTheme={setTheme}
          t={t}
          theme={theme}
        />
      </section>
    );
  }
  // The partner application page (design "Tinkr Auth" A5) is also
  // standalone: its own dark topbar replaces the public nav, and the page
  // ends after the application form (no public footer, per the design).
  if (screen === "api") {
    return (
      <section className="public-shell is-warm-api">
        <ApiAccessSplash
          locale={locale}
          session={session}
          setAuthMode={setAuthMode}
          setLocale={setLocale}
          setScreen={setScreen}
          t={t}
        />
      </section>
    );
  }
  return (
    <section className="public-shell is-warm-splash">
      <SplashSkipLink t={t} />
      <PublicNav
        locale={locale}
        isAuthenticated={authStatus === "authenticated"}
        setAuthMode={setAuthMode}
        setLocale={setLocale}
        setScreen={setScreen}
        setTheme={setTheme}
        t={t}
        theme={theme}
      />
      <SplashScreen
        locale={locale}
        docsLabel={docsNavLabel(locale)}
        isAuthenticated={authStatus === "authenticated"}
        setAuthMode={setAuthMode}
        setScreen={setScreen}
        t={t}
      />
    </section>
  );
}

function PublicNav({
  hideAuthAction = false,
  isAuthenticated,
  locale,
  setAuthMode,
  setLocale,
  setScreen,
  setTheme,
  t,
  theme
}: {
  hideAuthAction?: boolean;
  isAuthenticated: boolean;
  locale: SupportedLocale;
  setAuthMode: (mode: AuthMode) => void;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  setTheme: (theme: Theme) => void;
  t: Translate;
  theme: Theme;
}) {
  const navLabel = docsNavLabel(locale);
  // Scroll-aware chrome: transparent while the page is at the top so the
  // bar and the hero read as one panel; tinted + blurred once content
  // scrolls underneath (`.public-nav.is-scrolled` in styles.css).
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={scrolled ? "public-nav is-scrolled" : "public-nav"}>
      <button className="brand-lockup link-reset" onClick={() => setScreen("splash")} type="button">
        <BrandLogo />
      </button>

      <div className="public-nav-actions">
        <nav className="public-nav-links" aria-label={navLabel}>
          <a className="nav-link" href="/docs">
            {navLabel}
          </a>
        </nav>
        <QuickPrintNavLink t={t} />
        {hideAuthAction ? null : isAuthenticated ? (
          <button
            className="text-button public-nav-dashboard"
            aria-label={t("nav.dashboard")}
            onClick={() => setScreen("dashboard")}
            title={t("nav.dashboard")}
            type="button"
          >
            <Gauge size={16} />
            <span>{t("nav.dashboard")}</span>
          </button>
        ) : (
          <button
            className="text-button"
            aria-label={t("nav.login")}
            onClick={() => {
              setAuthMode("login");
              setScreen("auth");
            }}
            title={t("nav.login")}
            type="button"
          >
            <LockKeyhole size={16} />
            <span>{t("nav.login")}</span>
          </button>
        )}
        {hideAuthAction ? null : (
          <SplashNavCta isAuthenticated={isAuthenticated} setAuthMode={setAuthMode} setScreen={setScreen} t={t} />
        )}
        <LanguageSelect locale={locale} setLocale={setLocale} t={t} />
        <button
          className="icon-button"
          type="button"
          title={theme === "dark" ? t("nav.themeLight") : t("nav.themeDark")}
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>
    </header>
  );
}

function LanguageSelect({
  compact = false,
  locale,
  setLocale,
  t
}: {
  compact?: boolean;
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: Translate;
}) {
  return (
    <label className={compact ? "language-select compact" : "language-select"}>
      <span>{t("nav.language")}</span>
      <select
        aria-label={t("nav.language")}
        onChange={(event) => setLocale(event.target.value as SupportedLocale)}
        value={locale}
      >
        {supportedLocales.map((code) => (
          <option key={code} value={code}>
            {localeMeta[code].nativeLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function ApiAccessSplash({
  locale,
  session,
  setAuthMode,
  setLocale,
  setScreen,
  t
}: {
  locale: SupportedLocale;
  session: Session | null;
  setAuthMode: (mode: AuthMode) => void;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  t: Translate;
}) {
  const loginWithAccess = () => {
    setAuthMode("login");
    setScreen("auth");
  };

  // Design "Tinkr Auth" A5: the partner application page ships a single dark
  // colorway in both themes (a developer-lane identity, like the console's
  // LIVE MODE chrome). The `.wsf-api` scope pins the dark palette.
  return (
    <section className="wsf-api">
      <header className="wsf-api-topbar">
        <button
          aria-label={BRAND.name}
          className="wsf-api-logo link-reset"
          onClick={() => setScreen("splash")}
          type="button"
        >
          <img alt="" src={BRAND.logos.fullColorHorizontal} />
        </button>
        <div className="wsf-api-topbar-actions">
          <a className="wsf-api-toplink" href="/docs">
            {t("api.docsLink")}
          </a>
          <LanguageSelect compact locale={locale} setLocale={setLocale} t={t} />
          <button className="wsf-api-loginpill" onClick={loginWithAccess} type="button">
            {t("api.loginWithAccess")}
          </button>
        </div>
      </header>

      <div className="partner-intake-layout">
        <div className="partner-intake-intro">
          <span className="wsf-api-chip">{t("partnerApplication.eyebrow")}</span>
          <h1>{t("partnerIntake.title")}</h1>
          <p>{t("partnerIntake.intro")}</p>
          <p className="wsf-api-soon">{t("api.comingSoonNotice")}</p>
          <ol className="partner-intake-journey">
            {[
              ["partnerIntake.request", "partnerIntake.requestHelp"],
              ["partnerIntake.build", "partnerIntake.buildHelp"],
              ["partnerIntake.launch", "partnerIntake.launchHelp"]
            ].map(([title, body], index) => (
              <li key={title}><span aria-hidden="true">0{index + 1}</span><div>
                <strong>{t(title as Parameters<Translate>[0])}</strong>
                <p>{t(body as Parameters<Translate>[0])}</p>
              </div></li>
            ))}
          </ol>
          <a href="/docs#for-organizations">{t("partnerIntake.program")}</a>
        </div>
        <div className="wsf-api-formwrap">
          <PartnerApplicationForm publicMode locale={locale} session={session} t={t} />
          <p className="wsf-api-already">
            {t("api.alreadyApproved")}{" "}
            <button className="wsf-link" onClick={loginWithAccess} type="button">
              {t("api.loginWithAccess")}
            </button>
          </p>
          <StandaloneConsentSettings t={t} />
        </div>
      </div>
    </section>
  );
}

function AuthScreen({
  authMode,
  authStatus,
  locale,
  onPasswordSetupComplete,
  session,
  setAuthMode,
  setLocale,
  setScreen,
  setTheme,
  t,
  theme
}: {
  authMode: AuthMode;
  authStatus: AuthStatus;
  locale: SupportedLocale;
  onPasswordSetupComplete: () => void;
  session: Session | null;
  setAuthMode: (mode: AuthMode) => void;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  setTheme: (theme: Theme) => void;
  t: Translate;
  theme: Theme;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [accountCountryCode, setAccountCountryCode] = useState("");
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const countryEdited = useRef(false);
  const [signupCountries, setSignupCountries] = useState<string[] | null>(null);
  const [signupCountriesFailed, setSignupCountriesFailed] = useState(false);
  useEffect(preloadForAuthScreen, []);
  useEffect(() => {
    if (authMode !== "signup") return;
    let active = true;
    setSignupCountries(null);
    setSignupCountriesFailed(false);
    void fetchProviderSignupCountryHint().then(country => { if (active) setDetectedCountry(country); });
    void fetchProviderSignupCountries().then(
      (countries) => { if (active) setSignupCountries(countries); },
      () => { if (active) setSignupCountriesFailed(true); }
    );
    return () => { active = false; };
  }, [authMode]);
  useEffect(() => {
    if (!countryEdited.current && detectedCountry && signupCountries?.includes(detectedCountry)) {
      setAccountCountryCode(detectedCountry);
    }
  }, [detectedCountry, signupCountries]);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [legalConsentChecked, setLegalConsentChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupConfirmationEmail, setSignupConfirmationEmail] = useState<string | null>(null);
  const signupConfirmationHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (signupConfirmationEmail) signupConfirmationHeadingRef.current?.focus();
  }, [signupConfirmationEmail]);
  const signupCountryMissing =
    authMode === "signup" && (!isIsoCountryCode(accountCountryCode) ||
      !signupCountries?.includes(accountCountryCode));
  const signupLegalConsentMissing =
    authMode === "signup" && !legalConsentChecked;
  const signupLegalUnavailable = authMode === "signup" && isLegalSignupBlocked();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setStatus(null);

    if (!hasSupabaseConfig) {
      setError(t("auth.missingConfig"));
      return;
    }

    if (authMode === "forgot") {
      setSubmitting(true);
      try {
        await requestProviderPasswordReset(email);
        setStatus(t("auth.passwordResetSent"));
      } catch (err) {
        setError(humanReadableAuthError(err, t));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (isPasswordSetupMode(authMode)) {
      if (authStatus !== "authenticated" || !session) {
        setError(
          authMode === "invite"
            ? t("auth.inviteSetupLinkRequired")
            : t("auth.passwordResetLinkRequired")
        );
        return;
      }
      if (password.length < 8) {
        setError(t("auth.passwordTooShort"));
        return;
      }
      if (password !== confirmPassword) {
        setError(t("auth.passwordMismatch"));
        return;
      }
      setSubmitting(true);
      try {
        await updateProviderPassword(password);
        // The recovery link signed the user in to a short-lived recovery
        // session so `updateUser({ password })` could run. After the new
        // password lands, force a sign-out and bounce the user back to
        // the login tab so they have to prove they actually have the new
        // password — leaving them on the dashboard would defeat the
        // verification step the email link is supposed to gate.
        //
        // The SIGNED_OUT listener (above) reacts to signOut() by landing
        // the user on the public splash. Override that here so the user
        // sees the login tab pre-populated with their email instead of
        // having to find their way back to the auth screen. Same user, same
        // device: keep its instant-alert registration.
        await signOut({ releaseDeviceState: false });
        onPasswordSetupComplete();
        setPassword("");
        setConfirmPassword("");
        setStatus(
          authMode === "invite"
            ? t("auth.invitePasswordUpdated")
            : t("auth.passwordUpdated")
        );
        setAuthMode("login");
        setScreen("auth");
      } catch (err) {
        setError(humanReadableAuthError(err, t));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (signupCountryMissing) {
      setError(t(signupCountriesFailed ? "auth.countryCheckFailed" :
        signupCountries === null ? "auth.countryChecking" :
        !isIsoCountryCode(accountCountryCode) ? "auth.countryRequired" : "auth.countryUnavailable"));
      return;
    }
    if (signupLegalUnavailable) {
      setError(t("auth.legalDraftUnavailable"));
      return;
    }
    if (signupLegalConsentMissing) {
      setError(t("auth.legalConsentRequired"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.passwordTooShort"));
      return;
    }

    setSubmitting(true);

    try {
      if (authMode === "signup") {
        await signUpProviderWithPassword({
          email,
          password,
          companyName,
          accountCountryCode
        });
        setPassword("");
        setConfirmPassword("");
        setShowPassword(false);
        setSignupConfirmationEmail(email);
      } else {
        // The console chunk downloads while the credentials are checked
        // (P-01). Not earlier: its stylesheet brings font faces the sign-in
        // form's styles name, so loading it while the form is idle on screen
        // swaps the form's fonts in front of the reader.
        preloadConsoleForSignIn();
        await signInProviderWithPassword({ email, password });
        setStatus(t("auth.loginSuccess"));
      }
    } catch (err) {
      setError(humanReadableAuthError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  const switchMode = (mode: AuthMode) => {
    setAuthMode(mode);
    setSignupConfirmationEmail(null);
    setStatus(null);
    setError(null);
  };

  // Teal brand-panel copy: signup pitches acquisition, every other mode
  // (login / forgot / reset / invite) carries the sign-in framing.
  const panel =
    authMode === "signup"
      ? {
          line1: t("auth.panelSignupTitle1"),
          line2: t("auth.panelSignupTitle2"),
          body: t("auth.panelSignupBody")
        }
      : {
          line1: t("auth.panelLoginTitle1"),
          line2: t("auth.panelLoginTitle2"),
          body: t("auth.panelLoginBody")
        };

  const heading =
    authMode === "forgot" || authMode === "reset"
      ? t("auth.passwordResetTitle")
      : authMode === "invite"
        ? t("auth.inviteSetupTitle")
        : authMode === "signup"
          ? t("auth.signupTitle")
          : t("auth.loginTitle");

  // Body line under the H1 for the email-link modes; login/signup render
  // the mode-switch link row instead.
  const modeBody =
    authMode === "forgot"
      ? t("auth.passwordResetBody")
      : authMode === "invite"
        ? authStatus === "authenticated"
          ? t("auth.inviteSetupReadyBody")
          : t("auth.inviteSetupLinkRequired")
        : authMode === "reset"
          ? authStatus === "authenticated"
            ? t("auth.passwordResetReadyBody")
            : t("auth.passwordResetLinkRequired")
          : null;

  return (
    <section className="warm-auth" data-testid="auth-screen">
      {/* Teal brand panel (design "Tinkr Auth" A1–A4): logo mark links back
          to the splash, diamond trio, mode headline, mono tagline. Same
          deep teal in both themes; the full-color logo is used on dark too
          (design note — no mono-white swap). */}
      <aside className="warm-auth-panel">
        <button
          aria-label={BRAND.name}
          className="warm-auth-logo link-reset"
          onClick={() => setScreen("splash")}
          type="button"
        >
          <img alt="" src={BRAND.logos.fullColorMark} />
        </button>
        <div className="warm-auth-panel-copy">
          <span aria-hidden="true" className="warm-auth-diamonds">
            <i />
            <i />
            <i />
          </span>
          <p className="warm-auth-panel-title">
            {panel.line1}
            <br />
            {panel.line2}
          </p>
          <p className="warm-auth-panel-body">{panel.body}</p>
        </div>
        <p className="warm-auth-tagline">{t("auth.panelTagline")}</p>
      </aside>

      <div className="warm-auth-main">
        {/* Discreet locale/theme row: users landing here straight from a
            reset/invite email never pass the splash nav, so language and
            theme must stay reachable (founder decision, 2026-07-02). */}
        <div className="warm-auth-prefs">
          <LanguageSelect compact locale={locale} setLocale={setLocale} t={t} />
          <button
            className="icon-button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            title={theme === "dark" ? t("nav.themeLight") : t("nav.themeDark")}
            type="button"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
        </div>

        {authMode === "signup" && signupConfirmationEmail ? (
          <section aria-labelledby="signup-confirmation-title" className="warm-auth-card warm-auth-confirmation">
            <span aria-hidden="true" className="warm-auth-confirmation-mark"><Mail size={22} /></span>
            <h1 id="signup-confirmation-title" ref={signupConfirmationHeadingRef} tabIndex={-1}>
              {t("auth.checkEmailTitle")}
            </h1>
            <p className="warm-auth-email">{signupConfirmationEmail}</p>
            <p className="warm-auth-switch">{t("auth.signupConfirmationBody")}</p>
            <button className="warm-auth-submit" onClick={() => switchMode("login")} type="button">
              <span>{t("auth.signInWithPassword")}</span>
              <ArrowRight aria-hidden="true" size={17} />
            </button>
          </section>
        ) : (
        <form className="warm-auth-card" onSubmit={submit}>
          {/*
            MERGED-SEC-004 round 3 (Codex on PR #550): OAuth surface is
            intentionally not rendered yet — the "Tinkr Auth" design's
            "Continue with Google" button stays omitted. Supabase's
            signInWithOAuth creates new auth users regardless of UI mode
            (login vs signup only changed which form rendered), and there's
            no client-side option to gate that. A login-mode OAuth click for
            a brand-new user would still create an account WITHOUT the
            country / company_name / account_intent metadata the rest of
            provider onboarding depends on, bypassing the country capability
            gate. The safe path is a post-callback bootstrap that detects
            first-time OAuth users and routes them through a completion
            screen that captures the missing metadata — that's a future
            PR. Until then, email/password is the only auth method exposed
            in the UI.

            The signInWithGoogle / signInWithApple helpers in
            ../lib/supabase.ts stay so the future bootstrap PR has the
            client-side primitives ready. The auth.continueWithGoogle /
            auth.continueWithApple / auth.or i18n keys also stay across
            all 11 locales for the same reason.
          */}
          <h1>{heading}</h1>

          {authMode === "login" ? (
            <p className="warm-auth-switch">
              {t("auth.newToTinkr")}{" "}
              <button className="wsf-link" onClick={() => switchMode("signup")} type="button">
                {t("auth.createAccountLink")}
              </button>
            </p>
          ) : authMode === "signup" ? (
            <p className="warm-auth-switch">
              {t("auth.alreadyHaveAccount")}{" "}
              <button className="wsf-link" onClick={() => switchMode("login")} type="button">
                {t("auth.signInLink")}
              </button>
            </p>
          ) : (
            <p className="warm-auth-switch">
              {modeBody}
              {/* Authenticated reset/invite sessions get NO escape link:
                  the email-link flow must end in a password update, not a
                  back-out into the dashboard (regression guard from the
                  pre-redesign AuthScreen). */}
              {authMode === "forgot" || authStatus !== "authenticated" ? (
                <>
                  {" "}
                  <button
                    className="wsf-link"
                    onClick={() => switchMode("login")}
                    type="button"
                  >
                    {t("auth.backToLogin")}
                  </button>
                </>
              ) : null}
            </p>
          )}

          {authMode === "signup" ? (
            <label className="warm-field">
              <span className="warm-field-label">{t("auth.businessName")}</span>
              <input
                autoComplete="organization"
                onChange={(event) => setCompanyName(event.target.value)}
                placeholder={t("auth.businessNamePlaceholder")}
                required
                value={companyName}
              />
            </label>
          ) : null}

          {authMode === "signup" ? (
            <div className="warm-field">
              <ProviderSignupCountryPicker value={accountCountryCode} countries={signupCountries}
                onChange={country => { countryEdited.current = true; setAccountCountryCode(country); }}
                locale={locale} t={t} disabled={submitting} />
              <span className="warm-field-help" role="status">
                {signupCountriesFailed ? t("auth.countryCheckFailed") :
                  signupCountries === null ? t("auth.countryChecking") :
                  signupCountries.length === 0 ? t("auth.countryNoneOpen") :
                  accountCountryCode && !signupCountries.includes(accountCountryCode)
                    ? t("auth.countryUnavailable") : null}
              </span>
            </div>
          ) : null}

          {isPasswordSetupMode(authMode) ? null : (
            <label className="warm-field">
              <span className="warm-field-label">{t("auth.businessEmail")}</span>
              <input
                autoComplete="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                required
                type="email"
                value={email}
              />
            </label>
          )}

          {authMode === "forgot" ? null : (
            <div className="warm-field">
              <span className="warm-field-labelrow">
                <label className="warm-field-label" htmlFor="auth-password">
                  {isPasswordSetupMode(authMode) ? t("auth.newPassword") : t("auth.password")}
                </label>
                {authMode === "login" ? (
                  <button
                    className="wsf-link"
                    onClick={() => switchMode("forgot")}
                    type="button"
                  >
                    {t("auth.forgotShort")}
                  </button>
                ) : null}
              </span>
              <span className="warm-field-inputwrap">
                <input
                  autoComplete={authMode === "login" ? "current-password" : "new-password"}
                  id="auth-password"
                  minLength={8}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t("auth.passwordPlaceholder")}
                  required
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <button
                  aria-pressed={showPassword}
                  className="warm-field-show"
                  onClick={() => setShowPassword((value) => !value)}
                  type="button"
                >
                  {showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                </button>
              </span>
              {authMode === "signup" ? (
                <span className="warm-field-help">{t("auth.passwordHelp")}</span>
              ) : null}
            </div>
          )}

          {isPasswordSetupMode(authMode) ? (
            <label className="warm-field">
              <span className="warm-field-label">{t("auth.confirmPassword")}</span>
              <input
                autoComplete="new-password"
                minLength={8}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder={t("auth.confirmPasswordPlaceholder")}
                required
                type="password"
                value={confirmPassword}
              />
            </label>
          ) : null}

          {authMode === "signup" ? (
            <label className="warm-field warm-consent">
              <input
                checked={legalConsentChecked}
                disabled={signupLegalUnavailable}
                onChange={(event) => setLegalConsentChecked(event.target.checked)}
                required
                type="checkbox"
              />
              <span>
                {renderLegalConsentText(t("auth.legalConsent"), {
                  terms: t("auth.legalConsentTerms"),
                  privacy: t("auth.legalConsentPrivacy")
                })}
              </span>
            </label>
          ) : null}

          {signupLegalUnavailable ? (
            <p className="form-note error">{t("auth.legalDraftUnavailable")}</p>
          ) : null}

          {!hasSupabaseConfig ? (
            <p className="form-note warn">{t("auth.missingConfig")}</p>
          ) : null}
          {status ? <p className="form-note good">{status}</p> : null}
          {error ? <p className="form-note error">{error}</p> : null}

          <button
            className="warm-auth-submit"
            disabled={
              submitting ||
              !hasSupabaseConfig ||
              signupCountryMissing ||
              signupLegalUnavailable ||
              signupLegalConsentMissing
            }
            type="submit"
          >
            <span>
              {submitting
                ? t("auth.sending")
                : authMode === "signup"
                  ? t("auth.createAccount")
                  : authMode === "forgot"
                    ? t("auth.sendSecureLink")
                    : authMode === "invite"
                      ? t("auth.setPassword")
                    : authMode === "reset"
                      ? t("auth.updatePassword")
                      : t("auth.signInWithPassword")}
            </span>
            <ArrowRight aria-hidden="true" size={17} />
          </button>

          {isPasswordSetupMode(authMode) && status ? (
            <button
              className="warm-auth-secondary"
              onClick={() => setScreen("dashboard")}
              type="button"
            >
              <span>{t("nav.returnDashboard")}</span>
              <Gauge size={17} />
            </button>
          ) : null}
        </form>
        )}
        <StandaloneConsentSettings t={t} />
      </div>
    </section>
  );
}

type DashboardView = "provider" | "admin" | "partner";

type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

type MessageKey = Parameters<Translate>[0];

type NavItem = {
  id: string;
  labelKey: MessageKey;
  icon: LucideIcon;
  /** i18n key for the page-header eyebrow when this section is active. */
  eyebrowKey: MessageKey;
  /** i18n key for the page-header headline. */
  titleKey: MessageKey;
  /** Render the metric strip on this section? Default false. */
  showMetrics?: boolean;
  /**
   * Warm-workshop sidebar group label (OVERVIEW / SHOP SETUP / MONEY /
   * DEVELOPER / ACCOUNT). Consecutive items sharing a groupKey render under
   * one label; admin nav has no groups (its shell owns its own tabs).
   */
  groupKey?: MessageKey;
};

const NAV_ITEMS: Record<DashboardView, NavItem[]> = {
  provider: [
    {
      id: "dashboard",
      labelKey: "stitch.nav.provider.dashboard",
      icon: Gauge,
      eyebrowKey: "stitch.page.provider.eyebrow",
      titleKey: "stitch.page.provider.title",
      groupKey: "stitch.nav.group.overview"
    },
    {
      id: "work_queue",
      labelKey: "stitch.nav.provider.workQueue",
      icon: Wrench,
      eyebrowKey: "workQueue.eyebrow",
      titleKey: "workQueue.title",
      groupKey: "stitch.nav.group.overview"
    },
    {
      id: "onboarding",
      labelKey: "stitch.nav.provider.onboarding",
      icon: ShieldCheck,
      eyebrowKey: "onboarding.eyebrow",
      titleKey: "onboarding.title",
      groupKey: "stitch.nav.group.shopSetup"
    },
    {
      id: "capabilities",
      labelKey: "stitch.nav.provider.capabilities",
      icon: Package,
      eyebrowKey: "capabilities.eyebrow",
      titleKey: "capabilities.title",
      groupKey: "stitch.nav.group.shopSetup"
    },
    {
      id: "rate_cards",
      labelKey: "stitch.nav.provider.rateCards",
      icon: Receipt,
      eyebrowKey: "rateCard.eyebrow",
      titleKey: "rateCard.title",
      groupKey: "stitch.nav.group.shopSetup"
    },
    {
      id: "operations",
      labelKey: "stitch.nav.provider.operations",
      icon: Layers,
      eyebrowKey: "operations.eyebrow",
      titleKey: "operations.title",
      groupKey: "stitch.nav.group.shopSetup"
    },
    {
      // Warm-workshop P4: payables + settlement history move out of the work
      // queue into their own MONEY section (design handoff, 2026-07).
      id: "payouts",
      labelKey: "stitch.nav.provider.payouts",
      icon: Wallet,
      eyebrowKey: "providerPayouts.eyebrow",
      titleKey: "providerPayouts.title",
      groupKey: "stitch.nav.group.money"
    },
    {
      // Section id stays "api_keys" for URL/bookmark stability; the surface
      // is now the integrations interest panel (plugin-key minting UI is
      // dormant until a first-party plugin exists).
      id: "api_keys",
      labelKey: "stitch.nav.provider.apiKeys",
      icon: Terminal,
      eyebrowKey: "integrations.eyebrow",
      titleKey: "integrations.title",
      groupKey: "stitch.nav.group.developer"
    },
    {
      id: "account",
      labelKey: "stitch.nav.account",
      icon: UserCog,
      eyebrowKey: "account.eyebrow",
      titleKey: "account.title",
      groupKey: "stitch.nav.group.account"
    }
  ],
  admin: [
    // Admin terminal sections (labels only; the admin shell owns its own
    // section strip, subtabs and pages — components/admin/adminSections.ts,
    // adminNavRegistry.ts). The first entry is the landing page. Old
    // section ids (the 4-tab layout and the pre-May sidenav) are resolved by
    // resolveAdminLocation() (components/admin/adminLocation.ts) so admin
    // bookmarks, emails and stored locations keep working.
    { id: "home", labelKey: "admin.tab.home", icon: Gauge, eyebrowKey: "admin.tab.home", titleKey: "admin.tab.home" },
    { id: "orders", labelKey: "admin.tab.orders", icon: Receipt, eyebrowKey: "admin.tab.orders", titleKey: "admin.tab.orders" },
    { id: "providers", labelKey: "admin.tab.providers", icon: ShieldCheck, eyebrowKey: "admin.tab.providers", titleKey: "admin.tab.providers" },
    { id: "partners", labelKey: "admin.tab.partners", icon: Package, eyebrowKey: "admin.tab.partners", titleKey: "admin.tab.partners" },
    { id: "money", labelKey: "admin.tab.money", icon: Wallet, eyebrowKey: "admin.tab.money", titleKey: "admin.tab.money" },
    { id: "countries", labelKey: "admin.tab.countries", icon: Ship, eyebrowKey: "admin.tab.countries", titleKey: "admin.tab.countries" },
    { id: "apps", labelKey: "admin.tab.apps", icon: Layers, eyebrowKey: "admin.tab.apps", titleKey: "admin.tab.apps" },
    { id: "system", labelKey: "admin.tab.system", icon: Wrench, eyebrowKey: "admin.tab.system", titleKey: "admin.tab.system" }
  ],
  partner: [
    {
      id: "overview",
      labelKey: "stitch.nav.partner.overview",
      icon: Gauge,
      eyebrowKey: "stitch.page.partner.eyebrow",
      titleKey: "stitch.page.partner.title",
      groupKey: "stitch.nav.group.overview"
    },
    {
      id: "orders",
      labelKey: "stitch.nav.partner.orders",
      icon: Receipt,
      eyebrowKey: "stitch.nav.partner.orders",
      titleKey: "partnerOrders.title",
      groupKey: "stitch.nav.group.overview"
    },
    {
      id: "shipping",
      labelKey: "stitch.nav.partner.shipping",
      icon: Ship,
      eyebrowKey: "stitch.nav.partner.shipping",
      titleKey: "partnerShipping.title",
      groupKey: "stitch.nav.group.overview"
    },
    {
      id: "secrets",
      labelKey: "stitch.nav.partner.secrets",
      icon: Terminal,
      eyebrowKey: "stitch.nav.partner.secrets",
      titleKey: "stitch.partner.secrets.title",
      groupKey: "stitch.nav.group.developer"
    },
    {
      id: "webhooks",
      labelKey: "stitch.nav.partner.webhooks",
      icon: Webhook,
      eyebrowKey: "stitch.nav.partner.webhooks",
      titleKey: "stitch.partner.webhooks.title",
      groupKey: "stitch.nav.group.developer"
    },
    {
      id: "sandbox",
      labelKey: "stitch.nav.partner.sandbox",
      icon: FlaskConical,
      eyebrowKey: "stitch.nav.partner.sandbox",
      titleKey: "sandboxPlayground.title",
      groupKey: "stitch.nav.group.developer"
    },
    {
      id: "docs",
      labelKey: "stitch.nav.partner.docs",
      icon: BookOpen,
      eyebrowKey: "stitch.nav.partner.docs",
      titleKey: "partnerDocs.title",
      groupKey: "stitch.nav.group.developer"
    },
    {
      id: "payouts",
      labelKey: "stitch.nav.partner.payouts",
      icon: Wallet,
      eyebrowKey: "stitch.nav.partner.payouts",
      titleKey: "partnerPayouts.title",
      groupKey: "stitch.nav.group.money"
    },
    {
      id: "account",
      labelKey: "stitch.nav.account",
      icon: UserCog,
      eyebrowKey: "account.eyebrow",
      titleKey: "account.title",
      groupKey: "stitch.nav.group.account"
    }
  ]
};

function findSection(view: DashboardView, sectionId: string): NavItem {
  return NAV_ITEMS[view].find((item) => item.id === sectionId) ?? NAV_ITEMS[view][0]!;
}

// Old admin URLs resolve in components/admin/adminLocation.ts (shared with
// AdminShell's subtab seeding); unit tests in adminLocation.test.ts.

/**
 * Resolve which console a verified session should land on. Provider remains
 * the default for authenticated users without an explicit admin/partner role.
 * Signed-out viewers never receive a dashboard view; the route guard handles
 * them before OperationsDashboard can mount.
 */
export function defaultViewFor(session: Session | null): DashboardView {
  const role = accountRoleForSession(session);
  if (role === "admin") return "admin";
  if (role === "partner") return "partner";
  return "provider";
}

function accountRoleMatchesView(role: AccountRole, view: DashboardView): boolean {
  return role === view;
}

/**
 * Decide whether a `?view=...` URL override should be honored.
 *
 * Vite cannot enforce this at a server-render boundary, so the client guard
 * is deliberately strict: a verified session can only mount the console that
 * matches its single resolved role. Local preview no longer bypasses this;
 * tests and demos must seed the appropriate session role.
 */
export function viewOverrideAllowed(
  override: DashboardView,
  session: Session | null
): boolean {
  const role = accountRoleForSession(session);
  return Boolean(role && accountRoleMatchesView(role, override));
}

function demoDataEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return import.meta.env.VITE_ENABLE_DEMO_DATA === "true" && !isProductionHost();
}

function OperationsDashboard(props: Parameters<typeof OperationsDashboardContent>[0]) {
  return <DashboardRefreshProvider><OperationsDashboardContent {...props} /></DashboardRefreshProvider>;
}

function OperationsDashboardContent({
  locale,
  session,
  setLocale,
  setScreen,
  setTheme,
  t,
  theme
}: {
  locale: SupportedLocale;
  session: Session;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  setTheme: (theme: Theme) => void;
  t: Translate;
  theme: Theme;
}) {
  const resolvedRole = resolveAccountRole(session);
  if (resolvedRole && !resolvedRole.ok) {
    return (
      <DashboardAccessBlocked
        locale={locale}
        onLogout={() => void signOut()}
        session={session}
        setLocale={setLocale}
        setScreen={setScreen}
        setTheme={setTheme}
        t={t}
        theme={theme}
      />
    );
  }

  // The console (view) is determined by the user's role on the session. A
  // real provider never sees an Admin console, a partner never sees a
  // Provider console — that's why we removed the in-UI switcher. The URL
  // ?view= parameter still works as a developer override (preview /
  // localhost) so we can still review every console without seeding fake
  // sessions per role.
  const defaultViewForSession = useMemo<DashboardView>(() => {
    return defaultViewFor(session);
  }, [session]);

  const [view, setView] = useState<DashboardView>(() => {
    if (typeof window === "undefined") return defaultViewForSession;
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("view");
    if (
      (fromQuery === "admin" || fromQuery === "partner" || fromQuery === "provider") &&
      viewOverrideAllowed(fromQuery, session)
    ) {
      return fromQuery;
    }
    return defaultViewForSession;
  });

  // When the session loads/changes (login, refresh, cross-tab signout) snap
  // to the canonical view for that user. If a URL override is present, only
  // honor it when the session role permits — otherwise it would be possible
  // to force the admin/partner console shell as a non-admin/non-partner
  // user via URL on production. Codex caught this on PR #496 review.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("view");
    if (
      (fromQuery === "admin" || fromQuery === "partner" || fromQuery === "provider") &&
      viewOverrideAllowed(fromQuery, session)
    ) {
      setView(fromQuery);
      return;
    }
    setView(defaultViewForSession);
  }, [session, defaultViewForSession]);

  // Drop cached dashboard data when the signed-in user changes (login, cross-tab
  // sign-out, account switch) so one account never reads another's cached
  // panels. Panels serve from this cache to avoid refetching on every dashboard
  // tab switch (lib/dashboard-resource + DashboardRefreshProvider below).
  const [section, setSection] = useState<string>(() => {
    if (typeof window === "undefined") return NAV_ITEMS["provider"][0]!.id;
    const params = new URLSearchParams(window.location.search);
    if (view === "admin") {
      // Resolve old admin URLs and stored locations (4-tab layout, pre-May
      // ids) at first render and normalise the address bar in place, so the
      // admin shell mounts straight on the requested page.
      const hint = params.has("section")
        ? null
        : restoreDashboardLocation(session.user.id, "admin", [...NAV_ITEMS.admin.map((item) => item.id), ...ADMIN_LEGACY_SECTION_IDS]);
      return normalizeAdminUrl(hint ? { section: hint.section, subtab: hint.subtab ?? null } : undefined).section;
    }
    if (params.has("section")) return params.get("section")!;
    const hint = restoreDashboardLocation(session.user.id, defaultViewForSession, NAV_ITEMS[defaultViewForSession].map((item) => item.id));
    if (hint?.subtab && !params.has("subtab")) {
      params.set("subtab", hint.subtab);
      window.history.replaceState(window.history.state, "", window.location.pathname + "?" + params.toString() + window.location.hash);
    }
    return hint?.section ?? NAV_ITEMS[defaultViewForSession][0]!.id;
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  // P-07: a section change keeps the current section on screen while the next
  // one's chunk loads (a thin progress bar instead of a >=300 ms spinner). The
  // nav highlights the target immediately.
  const [sectionPending, startSectionTransition] = useTransition();
  const [shownSection, setShownSection] = useOptimistic(section);
  const navigateSection = useCallback((next: string) => {
    setDrawerOpen(false);
    startSectionTransition(() => {
      setShownSection(next);
      setSection(next);
    });
  }, [setShownSection]);
  // The scrollable dashboard container, captured via callback ref so mobile
  // pull-to-refresh only arms when it is scrolled to the very top.
  const [mainEl, setMainEl] = useState<HTMLElement | null>(null);
  useDashboardScroll(mainEl, session.user.id + ":" + view + ":" + section);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [reauthChallenge, setReauthChallenge] =
    useState<AdminReauthChallenge | null>(null);
  const reauthPromiseRef = useRef<Promise<Session | null | void> | null>(null);
  const openDrawerFromEdge = useCallback(() => setDrawerOpen(true), []);

  useMobileEdgeSwipeDrawer({
    drawerOpen,
    onOpen: openDrawerFromEdge
  });

  // Coerce section to one valid for the active view; if a stale section was
  // hydrated from URL/localStorage that doesn't belong to the current view,
  // snap to the first entry.
  //
  // Admin view resolves old section ids (the 4-tab layout and the pre-May
  // sidenav: `general`, `shipping`, `control_room`, ...) with their subtab
  // onto the current (section, subtab) pair and rewrites the URL in place,
  // so operator bookmarks and emailed links keep working.
  useEffect(() => {
    const valid = NAV_ITEMS[view].some((item) => item.id === section);
    if (valid) return;
    if (view === "admin" && typeof window !== "undefined") {
      setSection(normalizeAdminUrl({ section }).section);
      return;
    }
    setSection(NAV_ITEMS[view][0]!.id);
  }, [view, section]);

  // The first sync only spells out where the console opened, so it replaces
  // the landing entry instead of adding a second one (ADR-WBC1, S-15).
  const sectionUrlSynced = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("tinkr-dashboard-view", view);
    rememberDashboardLocation(session.user.id, view, section);
    const firstSync = !sectionUrlSynced.current;
    sectionUrlSynced.current = true;
    // Sync ?view= and ?section= to URL together — they always move as a pair.
    const params = new URLSearchParams(window.location.search);
    const viewSame = params.get("view") === view;
    const sectionSame = params.get("section") === section;
    if (viewSame && sectionSame) return;
    params.set("view", view);
    params.set("section", section);
    const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    if (firstSync) window.history.replaceState({ ...historyStateObject(), view, section }, "", next);
    else window.history.pushState({ view, section }, "", next);
  }, [view, section, session.user.id]);

  // Restore view + section from URL on browser back/forward.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onPop() {
      const params = new URLSearchParams(window.location.search);
      const requestedView = params.get("view");
      const requestedSection = params.get("section");
      if (
        (requestedView === "admin" ||
          requestedView === "partner" ||
          requestedView === "provider") &&
        viewOverrideAllowed(requestedView, session)
      ) {
        setView(requestedView);
      }
      if (requestedView === "admin" && requestedSection && viewOverrideAllowed("admin", session)) {
        // An old admin pair (Back to a pre-change entry, or a bell link)
        // resolves here and is rewritten in place; AdminShell resolves the
        // same URL the same way, whichever listener runs first.
        setSection(normalizeAdminUrl().section);
      } else if (requestedSection) {
        setSection(requestedSection);
      }
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [session]);

  // Close drawer on view OR section change (mobile UX).
  useEffect(() => {
    setDrawerOpen(false);
  }, [view, section]);

  useEffect(() => {
    if (view !== "admin") {
      setTinkrApiReauthHandler(null);
      return;
    }

    setTinkrApiReauthHandler(({ purpose }) => {
      if (purpose !== "admin_action") {
        return Promise.reject(new Error("Unsupported reauth purpose"));
      }
      if (reauthPromiseRef.current) return reauthPromiseRef.current;

      const challenge = new Promise<Session | null>((resolve, reject) => {
        setReauthChallenge({
          resolve,
          reject: () => reject(new Error("reauth_cancelled"))
        });
      });
      reauthPromiseRef.current = challenge.finally(() => {
        reauthPromiseRef.current = null;
      });
      return reauthPromiseRef.current;
    });

    return () => {
      setTinkrApiReauthHandler(null);
    };
  }, [view]);

  // Close drawer on escape
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setDrawerOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  async function logout() {
    // Hard-navigate after signOut so any in-flight panel state, lazy imports,
    // or cached query results from /api are dropped at the document boundary
    // — same posture as apps/old-web AdminLayoutShell.
    await signOut();
    if (typeof window !== "undefined") {
      window.location.assign("/");
      return;
    }
    setScreen("splash");
  }

  // Admin chrome is an operator-only surface and always renders in English.
  // Provider and partner chrome follow the active locale.
  const adminT: Translate = useMemo(() => (key) => translate("en", key), []);
  const chromeT: Translate = view === "admin" ? adminT : t;

  // Demo mode is explicitly env-gated. Production and staging builds must not
  // set VITE_ENABLE_DEMO_DATA; ?demo=1 is ignored unless the build opted in.
  const demoMode = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return demoDataEnabled() && params.get("demo") === "1";
  }, []);

  const activeSection = findSection(view, section);
  const partnerShopStatus = useWarmShopStatus(view, session, t);
  // Provider console state (ADR-W-E1): one derived display state drives the
  // sidebar chip, the state-aware navigation and the lockouts. The shell owns
  // these reads and passes the state to AccessDashboard (one reader each).
  const providerConsole = useProviderConsoleState(view === "provider" ? session : null);
  const providerStage = providerNavigationStage(providerConsole.display);
  // The offer badge and the "(n)" title subscribe to the offers cache in leaf
  // components, so a poll never re-renders the whole console.
  const offerBadgeEnabled = view === "provider" && providerStage === "approved";
  const providerNavGroups =
    view === "provider"
      ? buildProviderNavGroups(
          NAV_ITEMS.provider,
          providerStage,
          <PendingOfferBadge enabled={offerBadgeEnabled} session={session} t={chromeT} variant="nav" />
        )
      : null;
  const providerStateChip = view === "provider" ? providerChip(providerConsole.display, t) : null;
  const shopStatus: WarmShopStatus | null =
    view === "provider"
      ? providerStateChip
        ? { ...providerStateChip, shopName: providerConsole.shop?.displayName || null }
        : null
      : partnerShopStatus;
  const drawerRef = useDrawerFocusManagement(drawerOpen, () => setDrawerOpen(false));

  return (
    <section
      className="provider-dashboard stitch-shell"
      data-view={view}
      data-drawer={drawerOpen ? "open" : "closed"}
    >
      <SkipToContentLink t={chromeT} />
      <ConsoleDocumentTitle
        enabled={view === "provider"}
        sectionLabel={chromeT(activeSection.id === "dashboard" && view === "provider" ? "providerShell.nav.home" : activeSection.labelKey)}
        session={offerBadgeEnabled ? session : null}
      />
      <StitchTopBar
        canChangePassword={sessionHasPasswordCredential(session)}
        drawerOpen={drawerOpen}
        refreshControl={view === "provider" ? <DashboardRefreshButton locale={locale} t={chromeT} /> : null}
        locale={locale}
        onChangePassword={() => setPasswordDialogOpen(true)}
        onLogout={() => void logout()}
        onNavigateSection={navigateSection}
        onOpenFeedback={() => setFeedbackDialogOpen(true)}
        onToggleDrawer={() => setDrawerOpen((open) => !open)}
        session={session}
        setLocale={setLocale}
        setScreen={setScreen}
        setTheme={setTheme}
        t={t}
        theme={theme}
        view={view}
      />

      <div className="stitch-body">
        <div
          aria-hidden="true"
          className="stitch-drawer-scrim"
          onClick={() => setDrawerOpen(false)}
        />
        {view === "admin" ? null : (
          <StitchSideNav
            activeSectionId={view === "provider" ? providerNavHighlight(shownSection, providerStage) : shownSection}
            chromeT={chromeT}
            drawerOpen={drawerOpen}
            groupsOverride={providerNavGroups}
            navRef={drawerRef}
            onSelectSection={navigateSection}
            shopStatus={shopStatus}
            t={t}
            view={view}
          />
        )}

        <main className="stitch-main" id="stitch-main" ref={setMainEl} tabIndex={-1}>
          <ConsoleNavProgress active={sectionPending} label={chromeT("access.dashboardLoading")} />
          <Suspense fallback={<ConsoleSkeleton label={t("access.dashboardLoading")} shape={section === "dashboard" ? "home" : "list"} />}>
          {view === "admin" ? (
            <StitchErrorBoundary panelName="Admin terminal" t={chromeT}>
              <DashboardOperationalNotices
                locale={locale}
                session={session}
                t={chromeT}
                view={view}
              />
              <Suspense fallback={<div className="panel" role="status" aria-busy="true" data-testid="admin-shell-loading">{t("access.dashboardLoading")}</div>}>
              <AdminShell
                section={
                  // Resolve an old section id at render time so the first
                  // mount already shows the right section. Waiting for the
                  // section-coercion effect would let the fallback section's
                  // default-subtab resolution run first and clobber the
                  // requested page (child effects fire before parent effects).
                  isAdminSection(section)
                    ? section
                    : resolveAdminLocation(
                        section,
                        typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("subtab")
                      ).section
                }
                onSelectSection={setSection}
                session={session}
                t={chromeT}
              />
              </Suspense>
            </StitchErrorBoundary>
          ) : (
            <>
              {/* Only the provider dashboard reads the shared cache
                  (ProviderWorkQueuePanel via useDashboardResource). Partner
                  panels still fetch on mount through their own effects, so a
                  refresh button / pull-to-refresh there just bumps a token no
                  panel honors — render the control only where it does work. */}
              {view === "provider" ? (
                // The visible refresh control lives in the top bar (PUX-13);
                // this keeps mobile pull-to-refresh only.
                <DashboardRefreshControls desktopButton={false} scrollElement={mainEl} t={chromeT} />
              ) : null}
              {/* Warm-workshop P1 owns its page header (greeting + status
                  legend in both the active and day-one variants), so the
                  shell header would duplicate it. Partner overview (B1)
                  handles the same problem with a scoped :has() rule because
                  its own header only renders for active partners. */}
              {view === "provider" && activeSection.id === "dashboard" ? null : (
                <StitchErrorBoundary panelName="Page header" t={chromeT}>
                  <StitchPageHeader
                    demoMode={demoMode}
                    section={activeSection}
                    t={chromeT}
                    view={view}
                  />
                </StitchErrorBoundary>
              )}
              <DashboardOperationalNotices
                canDismiss={view === "provider" && providerConsole.display.operational}
                locale={locale}
                session={session}
                t={chromeT}
                view={view}
              />
              {/* RM-31: the metric strip is demo-fixture data only. Until real
                  per-account metrics are wired, it renders ONLY in demo mode —
                  a production user must never land on a row of "—" placeholder
                  tiles as their first dashboard impression. */}
              {activeSection.showMetrics && demoMode ? (
                <StitchErrorBoundary panelName="Metrics" t={chromeT}>
                  <StitchMetricStrip demoMode={demoMode} t={chromeT} view={view} />
                </StitchErrorBoundary>
              ) : null}

              {view === "partner" ? (
                <StitchErrorBoundary panelName="Partner hub" t={chromeT}>
                  <PartnerHubView
                    canChangePassword={sessionHasPasswordCredential(session)}
                    key={session?.user.id ?? "signed-out"}
                    locale={locale}
                    onChangePassword={() => setPasswordDialogOpen(true)}
                    onLogout={() => void logout()}
                    onNavigateSection={setSection}
                    onReportBug={() => setFeedbackDialogOpen(true)}
                    sectionId={section}
                    session={session}
                    t={t}
                  />
                </StitchErrorBoundary>
              ) : (
                <StitchErrorBoundary panelName="Provider dashboard" t={chromeT}>
                  <AccessDashboard
                    canChangePassword={sessionHasPasswordCredential(session)}
                    consoleState={view === "provider" ? providerConsole : undefined}
                    locale={locale}
                    onChangePassword={() => setPasswordDialogOpen(true)}
                    onLogout={() => void logout()}
                    onNavigateSection={navigateSection}
                    onReportBug={() => setFeedbackDialogOpen(true)}
                    section={section as never}
                    session={session}
                    t={t}
                  />
                </StitchErrorBoundary>
              )}
            </>
          )}
          </Suspense>
        </main>
      </div>

      {view === "provider" ? (
        <ProviderBottomTabBar
          activeSectionId={shownSection}
          drawerOpen={drawerOpen}
          offerBadge={<PendingOfferBadge enabled={offerBadgeEnabled} session={session} t={chromeT} variant="tab" />}
          onOpenMenu={() => setDrawerOpen((open) => !open)}
          onSelectSection={navigateSection}
          prefetchSection={prefetchProviderSection}
          stage={providerStage}
          t={chromeT}
        />
      ) : null}

      {passwordDialogOpen ? (
        <PasswordChangeDialog
          onClose={() => setPasswordDialogOpen(false)}
          session={session}
          t={t}
        />
      ) : null}

      {feedbackDialogOpen ? (
        <FeedbackDialog
          context={{ view, section }}
          locale={locale}
          onClose={() => setFeedbackDialogOpen(false)}
          session={session}
          t={t}
        />
      ) : null}

      {reauthChallenge ? (
        <AdminReauthDialog
          onCancel={() => {
            reauthChallenge.reject();
            setReauthChallenge(null);
          }}
          onComplete={(nextSession) => {
            reauthChallenge.resolve(nextSession);
            setReauthChallenge(null);
          }}
          session={session}
          t={chromeT}
        />
      ) : null}

      <StitchFooter t={t} />
    </section>
  );
}

function DashboardAccessBlocked({
  locale,
  onLogout,
  session,
  setLocale,
  setScreen,
  setTheme,
  t,
  theme
}: {
  locale: SupportedLocale;
  onLogout: () => void;
  session: Session;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  setTheme: (theme: Theme) => void;
  t: Translate;
  theme: Theme;
}) {
  return (
    <section className="provider-dashboard stitch-shell" data-view="provider">
      <StitchTopBar
        canChangePassword={sessionHasPasswordCredential(session)}
        drawerOpen={false}
        locale={locale}
        onChangePassword={() => undefined}
        onLogout={onLogout}
        onOpenFeedback={() => undefined}
        onToggleDrawer={() => undefined}
        session={session}
        setLocale={setLocale}
        setScreen={setScreen}
        setTheme={setTheme}
        t={t}
        theme={theme}
        view="provider"
      />
      <main className="stitch-main access-blocked-screen" data-testid="role-conflict-gate">
        <section className="panel">
          <span className="eyebrow">{t("access.roleConflictEyebrow")}</span>
          <h1>{t("access.roleConflictTitle")}</h1>
          <p>{t("access.roleConflictBody")}</p>
          <button className="secondary-button" onClick={onLogout} type="button">
            <span>{t("dashboard.signOut")}</span>
            <LogOut size={18} />
          </button>
        </section>
      </main>
      <StitchFooter t={t} />
    </section>
  );
}

export function DashboardOperationalNotices({
  canDismiss = false,
  locale,
  session,
  t,
  view
}: {
  /** Non-critical announcements can be dismissed (acknowledged) by an
   * active provider; the API rejects acknowledgements from inactive ones. */
  canDismiss?: boolean;
  locale: SupportedLocale;
  session: Session | null;
  t: Translate;
  view: DashboardView;
}) {
  const { refreshToken } = useDashboardRefresh();
  // P-04: when the console bootstrap already has the platform status and the
  // announcements, the notices render with the chrome's first frame instead
  // of being inserted above loaded content.
  const [platform, setPlatform] = useState<PlatformStatusResponse | null>(() =>
    session ? peekSettledConsolePrefetch<PlatformStatusResponse>("platform-status", session.user.id) ?? null : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyAckId, setBusyAckId] = useState<string | null>(null);
  const announcementResource = useDashboardResource({
    key: "provider-announcements:" + (session?.user.id ?? "anon") + ":" + locale,
    enabled: Boolean(session) && view === "provider",
    staleTimeMs: 300_000, refreshIntervalMs: 300_000,
    fetcher: () => listProviderAnnouncements(session!, locale)
  });
  const [announcements, setAnnouncements] = useState<OperationalAnnouncement[]>(() => announcementResource.data?.announcements ?? []);
  useEffect(() => {
    if (announcementResource.data) setAnnouncements(announcementResource.data.announcements);
  }, [announcementResource.data]);

  async function refreshNotices() {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const nextPlatform = await (takeConsolePrefetch<PlatformStatusResponse>("platform-status", session.user.id) ?? getPlatformStatus(session));
      setPlatform(nextPlatform);
    } catch (caught) {
      setError(messageFromOperationalError(caught, t));
      setPlatform(null);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!session) {
      setPlatform(null);
      setAnnouncements([]);
      setError(null);
      return;
    }
    void refreshNotices();
    // The global refresh control re-reads platform status too (PUX-13).
  }, [session, view, locale, refreshToken]);

  async function acknowledge(announcementId: string) {
    if (!session) return;
    setBusyAckId(announcementId);
    setError(null);
    try {
      const response = await acknowledgeProviderAnnouncement(session, announcementId);
      setAnnouncements((current) =>
        current.map((announcement) =>
          announcement.id === announcementId
            ? { ...announcement, acknowledgedAt: response.acknowledgedAt ?? new Date().toISOString() }
            : announcement
        )
      );
    } catch (caught) {
      setError(messageFromOperationalError(caught, t));
    } finally {
      setBusyAckId(null);
    }
  }

  // Critical notices stay pinned until acknowledged where required; other
  // banners can be dismissed, which records the same acknowledgement the
  // bell uses (PUX-14).
  const dismissible = (announcement: OperationalAnnouncement) =>
    canDismiss && announcement.severity !== "critical" && !announcement.requiresAck;
  const visibleAnnouncements = announcements.filter(
    (announcement) =>
      (announcement.displayMode === "banner" || announcement.displayMode === "modal") &&
      (announcement.requiresAck || announcement.severity === "critical" || !canDismiss
        ? !announcement.requiresAck || !announcement.acknowledgedAt
        : !announcement.acknowledgedAt)
  );
  const platformNotice =
    platform &&
    (platform.mode !== "normal" ||
      platform.activeMaintenance.length > 0 ||
      platform.scheduledMaintenance.length > 0 ||
      platform.actionBlocks.length > 0)
      ? platform
      : null;

  if (!platformNotice && visibleAnnouncements.length === 0 && !error) {
    return null;
  }

  return (
    <section
      aria-label={t("opsNotices.title")}
      aria-busy={loading || undefined}
      className="dashboard-operational-notices"
      aria-live="polite"
      data-compact={view === "provider" ? "true" : undefined}
    >
      {view === "provider" ? null : (
        <div className="dashboard-operational-notices-head">
          <div>
            <span className="eyebrow">{t("opsNotices.eyebrow")}</span>
            <h3>{t("opsNotices.title")}</h3>
          </div>
          <button
            aria-label={t("opsNotices.refresh")}
            className="stitch-icon-button"
            disabled={loading || !session}
            onClick={() => void refreshNotices()}
            type="button"
          >
            <RefreshCcw size={16} />
          </button>
        </div>
      )}

      {error ? (
        <div className="dashboard-operational-notice error" role="status">
          <AlertTriangle size={18} />
          <div>
            <strong>{t("opsNotices.errorTitle")}</strong>
            <span>{error}</span>
          </div>
        </div>
      ) : null}

      {platformNotice ? (
        <div className={`dashboard-operational-notice ${platformNotice.mode}`}>
          <AlertTriangle size={18} />
          <div>
            <strong>
              {platformNotice.activeMaintenance[0]?.publicTitle ??
                statusTitle(platformNotice.mode, t)}
            </strong>
            <span>
              {platformNotice.activeMaintenance[0]?.publicMessage ??
                scheduledMaintenanceCopy(platformNotice, t)}
            </span>
          </div>
        </div>
      ) : null}

      {visibleAnnouncements.map((announcement) => (
        <div
          className={`dashboard-operational-notice ${announcement.severity}`}
          key={announcement.id}
          role={announcement.severity === "critical" ? "alert" : "status"}
        >
          <Bell size={18} />
          <div>
            <strong>{announcement.title}</strong>
            <span>{announcement.body}</span>
            {announcement.requiresAck ? (
              <button
                className="button-link"
                disabled={busyAckId === announcement.id}
                onClick={() => void acknowledge(announcement.id)}
                type="button"
              >
                {busyAckId === announcement.id
                  ? t("opsNotices.ackSaving")
                  : t("opsNotices.acknowledge")}
              </button>
            ) : null}
          </div>
          {dismissible(announcement) ? (
            <button
              aria-label={t("providerShell.notice.dismiss").replace("{title}", announcement.title)}
              className="stitch-icon-button dashboard-operational-notice-dismiss"
              disabled={busyAckId === announcement.id}
              onClick={() => void acknowledge(announcement.id)}
              title={t("providerHome.action.dismiss")}
              type="button"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>
      ))}
    </section>
  );
}

function PasswordChangeDialog({
  onClose,
  session,
  t
}: {
  onClose: () => void;
  session: Session;
  t: Translate;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const email = session.user.email ?? "";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setStatus(null);

    if (!hasSupabaseConfig) {
      setError(t("auth.missingConfig"));
      return;
    }
    if (!email) {
      setError(t("auth.errorFallback"));
      return;
    }
    if (!currentPassword) {
      setError(t("auth.currentPasswordRequired"));
      return;
    }
    if (newPassword.length < 8) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("auth.passwordMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      await changeProviderPassword({ email, currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setStatus(t("auth.passwordUpdated"));
    } catch (caught) {
      setError(humanReadableAuthError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function sendResetLink() {
    setError(null);
    setStatus(null);

    if (!hasSupabaseConfig) {
      setError(t("auth.missingConfig"));
      return;
    }
    if (!email) {
      setError(t("auth.errorFallback"));
      return;
    }

    setResetSubmitting(true);
    try {
      await requestProviderPasswordReset(email);
      setStatus(t("auth.passwordResetSent"));
    } catch (caught) {
      setError(humanReadableAuthError(caught, t));
    } finally {
      setResetSubmitting(false);
    }
  }

  return (
    <div className="password-dialog-backdrop" role="presentation">
      <section
        aria-labelledby="password-dialog-title"
        aria-modal="true"
        className="password-dialog-card"
        role="dialog"
      >
        <div className="password-dialog-head">
          <div>
            <span className="stitch-eyebrow">{t("auth.changePassword")}</span>
            <h2 id="password-dialog-title">{t("auth.changePasswordTitle")}</h2>
          </div>
          <button
            aria-label={t("common.close")}
            className="stitch-icon-button"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </div>
        <p>{t("auth.changePasswordBody")}</p>
        <button
          className="button-link password-dialog-reset-link"
          disabled={resetSubmitting || submitting}
          onClick={() => void sendResetLink()}
          type="button"
        >
          {resetSubmitting ? t("auth.sending") : t("auth.dashboardResetLink")}
        </button>

        <form className="password-dialog-form" onSubmit={submit}>
          <label className="field">
            <span>{t("auth.currentPassword")}</span>
            <input
              autoComplete="current-password"
              onChange={(event) => setCurrentPassword(event.target.value)}
              placeholder={t("auth.currentPasswordPlaceholder")}
              required
              type="password"
              value={currentPassword}
            />
          </label>
          <label className="field">
            <span>{t("auth.newPassword")}</span>
            <input
              autoComplete="new-password"
              minLength={8}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder={t("auth.passwordPlaceholder")}
              required
              type="password"
              value={newPassword}
            />
          </label>
          <label className="field">
            <span>{t("auth.confirmPassword")}</span>
            <input
              autoComplete="new-password"
              minLength={8}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder={t("auth.confirmPasswordPlaceholder")}
              required
              type="password"
              value={confirmPassword}
            />
          </label>

          {status ? <p className="form-note good">{status}</p> : null}
          {error ? <p className="form-note error">{error}</p> : null}

          <div className="password-dialog-actions">
            <button className="secondary-button" onClick={onClose} type="button">
              {t("adminAnnouncements.cancel")}
            </button>
            <button
              className="primary-button"
              disabled={submitting || !hasSupabaseConfig}
              type="submit"
            >
              <span>{submitting ? t("auth.sending") : t("auth.updatePassword")}</span>
              <KeyRound size={18} />
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export function AdminReauthDialog({
  onCancel,
  onComplete,
  session,
  t
}: {
  onCancel: () => void;
  onComplete: (session: Session | null) => void;
  session: Session;
  t: Translate;
}) {
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const email = session.user.email ?? "";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setStatus(null);

    if (!hasSupabaseConfig) {
      setError(t("auth.missingConfig"));
      return;
    }
    if (!email) {
      setError(t("auth.errorFallback"));
      return;
    }
    if (!password) {
      setError(t("auth.currentPasswordRequired"));
      return;
    }

    setSubmitting(true);
    try {
      await createPasswordReauth(session, {
        email,
        password,
        purpose: "admin_action"
      });
      setPassword("");
      onComplete(session);
    } catch (caught) {
      setError(humanReadableAuthError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function sendResetLink() {
    setError(null);
    setStatus(null);

    if (!hasSupabaseConfig) {
      setError(t("auth.missingConfig"));
      return;
    }
    if (!email) {
      setError(t("auth.errorFallback"));
      return;
    }

    setResetSubmitting(true);
    try {
      await requestProviderPasswordReset(email);
      setStatus(t("auth.passwordResetSent"));
    } catch (caught) {
      setError(humanReadableAuthError(caught, t));
    } finally {
      setResetSubmitting(false);
    }
  }

  const portalTarget =
    document.querySelector<HTMLElement>(".stitch-shell") ?? document.body;

  return createPortal(
    <div
      className="password-dialog-backdrop admin-reauth-dialog-backdrop"
      data-admin-reauth-dialog="true"
      role="presentation"
    >
      <section
        aria-labelledby="admin-reauth-dialog-title"
        aria-modal="true"
        className="password-dialog-card"
        role="dialog"
      >
        <div className="password-dialog-head">
          <div>
            <span className="stitch-eyebrow">{t("access.adminReauthEyebrow")}</span>
            <h2 id="admin-reauth-dialog-title">{t("access.adminReauthTitle")}</h2>
          </div>
          <button
            aria-label={t("common.close")}
            className="stitch-icon-button"
            disabled={submitting}
            onClick={onCancel}
            type="button"
          >
            <X size={18} />
          </button>
        </div>
        <p>{t("access.adminReauthBody")}</p>
        <button
          className="button-link password-dialog-reset-link"
          disabled={resetSubmitting || submitting}
          onClick={() => void sendResetLink()}
          type="button"
        >
          {resetSubmitting ? t("auth.sending") : t("auth.dashboardResetLink")}
        </button>

        <form className="password-dialog-form" onSubmit={submit}>
          <label className="field">
            <span>{t("access.adminReauthPassword")}</span>
            <input
              autoComplete="current-password"
              autoFocus
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("access.adminReauthPasswordPlaceholder")}
              required
              type="password"
              value={password}
            />
          </label>

          {status ? <p className="form-note good">{status}</p> : null}
          {error ? <p className="form-note error">{error}</p> : null}

          <div className="password-dialog-actions">
            <button
              className="secondary-button"
              disabled={submitting}
              onClick={onCancel}
              type="button"
            >
              {t("adminAnnouncements.cancel")}
            </button>
            <button
              className="primary-button"
              disabled={submitting || !hasSupabaseConfig}
              type="submit"
            >
              <span>
                {submitting
                  ? t("access.adminReauthVerifying")
                  : t("access.adminReauthSubmit")}
              </span>
              <LockKeyhole size={18} />
            </button>
          </div>
        </form>
      </section>
    </div>,
    portalTarget
  );
}

function statusTitle(mode: PlatformStatusResponse["mode"], t: Translate): string {
  switch (mode) {
    case "incident":
      return t("opsNotices.incidentTitle");
    case "maintenance":
      return t("opsNotices.maintenanceTitle");
    case "degraded":
      return t("opsNotices.degradedTitle");
    case "normal":
    default:
      return t("opsNotices.normalTitle");
  }
}

function scheduledMaintenanceCopy(
  platform: PlatformStatusResponse,
  t: Translate
): string {
  if (platform.scheduledMaintenance.length > 0) {
    return platform.scheduledMaintenance[0]!.publicMessage;
  }
  if (platform.actionBlocks.length > 0) {
    return t("opsNotices.writeGateBody");
  }
  return t("opsNotices.statusBody");
}

function messageFromOperationalError(error: unknown, t: Translate): string {
  if (error instanceof TinkrApiError) {
    if (error.status === 401) return t("access.sessionExpired");
    if (error.status === 403) return t("access.noDashboardAccess");
    return describeApiErrorWithFallback(error, t, t("access.requestFailed"));
  }
  return t("access.requestFailed");
}


export function StitchTopBar({
  canChangePassword,
  drawerOpen,
  locale,
  onChangePassword,
  onLogout,
  onNavigateSection,
  onOpenFeedback,
  onToggleDrawer,
  refreshControl,
  session,
  setLocale,
  setScreen,
  setTheme,
  t,
  theme,
  view
}: {
  canChangePassword: boolean;
  drawerOpen: boolean;
  locale: SupportedLocale;
  onChangePassword: () => void;
  onLogout: () => void;
  /** Forwarded to the provider notifications bell so a notification's
   * actionUrl can jump straight to the relevant dashboard section. */
  onNavigateSection?: (section: string) => void;
  onOpenFeedback: () => void;
  onToggleDrawer: () => void;
  /** The console's single refresh control (provider view). */
  refreshControl?: ReactNode;
  session: Session | null;
  setLocale: (locale: SupportedLocale) => void;
  setScreen: (screen: Screen) => void;
  setTheme: (theme: Theme) => void;
  t: Translate;
  theme: Theme;
  view: DashboardView;
}) {
  // Inside the provider console the logo goes to Home, not the public
  // splash (PUX-33).
  const logoGoesHome = view === "provider" && Boolean(onNavigateSection);
  return (
    <header className="stitch-topbar">
      <div className="stitch-topbar-left">
        <button
          aria-expanded={drawerOpen}
          aria-label={drawerOpen ? t("stitch.closeMenu") : t("stitch.openMenu")}
          className="stitch-icon-button stitch-hamburger"
          onClick={onToggleDrawer}
          type="button"
        >
          {drawerOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
        <button
          aria-label={logoGoesHome ? `${BRAND.name} · ${t("providerShell.nav.home")}` : BRAND.name}
          className="stitch-brand"
          onClick={() => (logoGoesHome ? onNavigateSection?.("dashboard") : setScreen("splash"))}
          type="button"
        >
          <img alt={BRAND.name} src={BRAND.logos.fullColorHorizontal} />
        </button>
        {/*
         * MERGED-UX-002: removed the inert "Solutions" + "Network status" nav
         * buttons that had no onClick handlers. They mislead users into
         * thinking those panels exist. When real landing destinations are
         * built, wire them back here through `setScreen(...)`.
         */}
        {/* Warm workshop: logo · hairline divider · console name (desktop). */}
        {view === "provider" || view === "partner" ? (
          <>
            <span aria-hidden="true" className="warm-topbar-divider" />
            <span className="warm-console-name">
              {view === "partner"
                ? t("stitch.hub.partner")
                : t("stitch.hub.provider")}
            </span>
          </>
        ) : null}
      </div>
      <div className="stitch-topbar-right">
        {refreshControl ?? null}
        {view === "partner" ? (
          <button
            className="stitch-pill-button"
            onClick={() => setScreen("api")}
            type="button"
          >
            {t("nav.apiAccess")}
          </button>
        ) : null}
        {/* Warm workshop: quiet Docs / API reference text link in the topbar
            (design shared shell). Sidebar footer keeps the long-form link. */}
        {view === "provider" || view === "partner" ? (
          <a className="warm-topbar-link" href="/docs">
            {view === "partner" ? t("warm.topbar.apiReference") : t("warm.topbar.docs")}
          </a>
        ) : null}
        {/*
         * Admin renders warm-cream regardless of light/dark toggle —
         * the theme button would be a UI leak (clicks do nothing for
         * the admin terminal). Provider and partner views keep it.
         */}
        {view === "admin" ? null : (
          <button
            aria-label={theme === "dark" ? t("nav.themeLight") : t("nav.themeDark")}
            className="stitch-icon-button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            title={theme === "dark" ? t("nav.themeLight") : t("nav.themeDark")}
            type="button"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        )}
        {/*
         * Admin chrome is English-only (App.tsx adminT) — the locale
         * dropdown is a UI leak for that view because admin strings only
         * have `en` translations. Provider and partner views keep the
         * selector for their localized chrome.
         */}
        {view === "admin" ? null : (
          // The visible value + caret size to the selected language so the
          // control hugs its label instead of being pinned to the widest
          // option's width (which left the caret floating with a dead gap).
          // The native <select> is overlaid transparently for clicks,
          // keyboard, and screen-reader semantics.
          <span className="stitch-language">
            <Languages className="stitch-language-icon" size={16} aria-hidden="true" />
            <span className="stitch-language-value" aria-hidden="true">
              {localeMeta[locale].nativeLabel}
            </span>
            <ChevronDown className="stitch-language-caret" size={14} aria-hidden="true" />
            <label className="sr-only" htmlFor="stitch-locale">
              {t("nav.language")}
            </label>
            <select
              aria-label={t("nav.language")}
              className="stitch-language-native"
              id="stitch-locale"
              onChange={(event) => setLocale(event.target.value as SupportedLocale)}
              value={locale}
            >
              {supportedLocales.map((code) => (
                <option key={code} value={code}>
                  {localeMeta[code].nativeLabel}
                </option>
              ))}
            </select>
          </span>
        )}
        {/*
         * MERGED-PROV-004: notification bell wired to the provider
         * announcements stream. The provider bell only renders for provider
         * view; the partner view has its own session-scoped announcements bell.
         * The component manages its own state,
         * shows an unread badge, and opens a popover with per-row
         * Acknowledge buttons that hit the same /v1/provider/announcements/
         * {id}/ack endpoint DashboardOperationalNotices uses, so the two
         * surfaces stay in sync on the next refresh.
         */}
        {view === "provider" ? (
          <ProviderNotificationsBell
            locale={locale}
            onNavigateSection={onNavigateSection}
            session={session}
            t={t}
          />
        ) : null}
        {view === "partner" ? <PartnerAnnouncementsBell
          key={session?.user.id ?? "signed-out"}
          locale={locale}
          onNavigateSection={onNavigateSection}
          session={session}
          t={t}
        /> : null}
        {/* Admin bell (2026-08-09): fulfillment alerts — overdue orders and
          * provider withdrawals — written per-admin by the deadline sweep.
          * English-only, like the rest of the admin chrome. */}
        {view === "admin" ? <AdminNotificationsBell session={session} /> : null}
        {/* Provider/partner chrome keeps the top bar to the essentials on a
          * phone: feedback, password and sign-out live on the Account page
          * (AccountSettingsPanel). Admin chrome keeps the icon row. */}
        {session && view !== "admin" ? null : session ? (
          <>
            <button
              aria-label={t("feedback.openLabel")}
              className="stitch-icon-button"
              onClick={onOpenFeedback}
              title={t("feedback.openLabel")}
              type="button"
            >
              <MessageSquare size={18} />
            </button>
            {canChangePassword ? (
              <button
                aria-label={t("auth.changePassword")}
                className="stitch-icon-button"
                onClick={onChangePassword}
                title={t("auth.changePassword")}
                type="button"
              >
                <KeyRound size={18} />
              </button>
            ) : null}
            <button
              aria-label={t("dashboard.signOut")}
              className="stitch-icon-button"
              onClick={onLogout}
              title={t("dashboard.signOut")}
              type="button"
            >
              <LogOut size={18} />
            </button>
          </>
        ) : (
          <button
            aria-label={t("nav.login")}
            className="stitch-icon-button"
            onClick={() => setScreen("auth")}
            title={t("nav.login")}
            type="button"
          >
            <LockKeyhole size={18} />
          </button>
        )}
        {/* Warm workshop: account roundel (initials) closes the topbar.
            Teal for provider, ink for partner (design shared shell). */}
        {session && (view === "provider" || view === "partner") ? (
          <span aria-hidden="true" className="warm-avatar">
            {warmAvatarInitials(session)}
          </span>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Two-letter roundel initials from the session identity. Prefers the
 * user-facing display name when present, else the email local part.
 * Falls back to "T" (Tinkr) rather than rendering an empty circle.
 */
/**
 * Warm-workshop sidebar chip for the PARTNER console: LIVE MODE / SANDBOX /
 * IN REVIEW from the partner access state. The provider chip comes from the
 * derived provider display state (ProviderConsoleChrome.providerChip). While
 * the fetch is in flight (or for signed-out/admin views) this returns null
 * and the sidebar falls back to the plain console title.
 */
function useWarmShopStatus(
  view: DashboardView,
  session: Session | null,
  t: Translate
): WarmShopStatus | null {
  const userId = session?.user?.id ?? "anon";
  const partner = useDashboardResource<PartnerSelfOverview | null>({
    key: `warm-shop-status:partner:${userId}`,
    enabled: view === "partner" && session !== null,
    fetcher: async () => {
      if (!session) return null;
      try {
        return await getPartnerSelfOverview(session);
      } catch (error) {
        if (
          error instanceof TinkrApiError &&
          (error.status === 404 || error.status === 403 || error.status === 401)
        ) {
          return null;
        }
        throw error;
      }
    }
  });

  if (view === "partner") {
    const overview = partner.data;
    if (!overview) return null;
    const shopName = overview.partner.displayName || null;
    switch (overview.partner.state) {
      case "production_active":
        return { tone: "mono", label: t("warm.chip.liveMode"), shopName };
      case "sandbox_active":
        return { tone: "setup", label: t("warm.chip.sandbox"), shopName };
      case "production_review_pending":
        return { tone: "setup", label: t("warm.chip.inReview"), shopName };
      default:
        // suspended / terminated: the lifecycle panel owns that messaging.
        return null;
    }
  }

  return null;
}

function warmAvatarInitials(session: Session): string {
  const metadata = session.user.user_metadata as
    | { display_name?: unknown; full_name?: unknown }
    | null
    | undefined;
  const displayName =
    typeof metadata?.display_name === "string" && metadata.display_name.trim()
      ? metadata.display_name.trim()
      : typeof metadata?.full_name === "string" && metadata.full_name.trim()
        ? metadata.full_name.trim()
        : null;
  const source = displayName ?? session.user.email?.split("@")[0] ?? "";
  const words = source
    .split(/[\s._-]+/)
    .map((word) => word.trim())
    .filter(Boolean);
  if (words.length === 0) return "T";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

/**
 * Warm-workshop sidebar shop header: teal status chip above the shop /
 * org name (design handoff §shared shell). Chip + name are wired by the
 * screen owners (provider: shop open/paused from the profile; partner:
 * LIVE MODE / SANDBOX from the access stage) — when the data hasn't
 * loaded yet, nothing renders rather than a made-up status.
 */
export type WarmShopStatus = {
  /** Visual tone: "live" teal dot chip · "mono" mono LIVE-MODE chip ·
   * "setup" orange diamond chip for not-yet-live shops. */
  tone: "live" | "mono" | "setup" | "warn";
  label: string;
  shopName: string | null;
};

/** P-07: start a section's chunk on hover, keyboard focus or touch. */
function sectionPrefetchHandlers(section: string) {
  const prefetch = () => prefetchProviderSection(section);
  return { onFocus: prefetch, onPointerEnter: prefetch, onTouchStart: prefetch };
}

function StitchSideNav({
  activeSectionId,
  chromeT,
  drawerOpen = false,
  groupsOverride,
  navRef,
  onSelectSection,
  shopStatus,
  t,
  view
}: {
  activeSectionId: string;
  chromeT: Translate;
  drawerOpen?: boolean;
  /** Provider console: state-aware groups (PUX-15). */
  groupsOverride?: { groupKey?: MessageKey; items: Array<NavItem & { badge?: ReactNode }> }[] | null;
  navRef?: (node: HTMLElement | null) => void;
  onSelectSection: (sectionId: string) => void;
  shopStatus?: WarmShopStatus | null;
  t: Translate;
  view: DashboardView;
}) {
  const items = NAV_ITEMS[view];
  const hubTitle =
    view === "admin"
      ? chromeT("stitch.hub.admin")
      : view === "partner"
        ? chromeT("stitch.hub.partner")
        : chromeT("stitch.hub.provider");
  const warmChrome = view === "provider" || view === "partner";

  // Group consecutive items by groupKey (warm chrome only). Items without
  // a groupKey render in an unlabeled group.
  const groups: { groupKey?: MessageKey; items: Array<NavItem & { badge?: ReactNode }> }[] = groupsOverride ? [...groupsOverride] : [];
  for (const item of groupsOverride ? [] : items) {
    const last = groups[groups.length - 1];
    if (last && last.groupKey === item.groupKey) {
      last.items.push(item);
    } else {
      groups.push({ groupKey: item.groupKey, items: [item] });
    }
  }

  const renderItem = (item: NavItem & { badge?: ReactNode }) => {
    const isActive = item.id === activeSectionId;
    return (
      <li key={item.id}>
        <button
          aria-current={isActive ? "page" : undefined}
          className="stitch-nav-item"
          onClick={() => onSelectSection(item.id)}
          {...(view === "provider" ? sectionPrefetchHandlers(item.id) : {})}
          type="button"
        >
          <span className="stitch-icon" aria-hidden="true">
            <item.icon size={18} />
          </span>
          <span>{chromeT(item.labelKey)}</span>
          {item.badge ?? null}
        </button>
      </li>
    );
  };

  return (
    <aside
      aria-label={t("stitch.console.label")}
      aria-modal={drawerOpen ? "true" : undefined}
      className="stitch-sidenav"
      ref={navRef}
      role={drawerOpen ? "dialog" : undefined}
    >
      {warmChrome && shopStatus ? (
        <div className="warm-shop-block">
          <span
            className="warm-status-chip"
            data-tone={shopStatus.tone === "live" ? undefined : shopStatus.tone}
          >
            {shopStatus.tone === "setup" ? (
              <span aria-hidden="true" className="warm-diamond warm-diamond--accent" />
            ) : shopStatus.tone === "warn" ? (
              <span aria-hidden="true" className="warm-tri warm-tri--warn" />
            ) : (
              <span aria-hidden="true" className="warm-dot" />
            )}
            {shopStatus.label}
          </span>
          {shopStatus.shopName ? (
            <h2 className="warm-shop-name">{shopStatus.shopName}</h2>
          ) : null}
        </div>
      ) : (
        <div className="stitch-hub">
          <h2 className="stitch-hub-title">{hubTitle}</h2>
        </div>
      )}

      {warmChrome ? (
        groups.map((group, index) => (
          <div className="warm-nav-group" key={group.groupKey ?? `group-${index}`}>
            {group.groupKey ? (
              <p className="warm-nav-group-label">{chromeT(group.groupKey)}</p>
            ) : null}
            <ul className="stitch-nav-list">{group.items.map(renderItem)}</ul>
          </div>
        ))
      ) : (
        <ul className="stitch-nav-list">{items.map(renderItem)}</ul>
      )}


      <div className="stitch-sidenav-footer">
        <a
          className="stitch-nav-item"
          href="/docs"
        >
          <span className="stitch-icon" aria-hidden="true">
            <BookOpen size={16} />
          </span>
          <span>{t("stitch.sidenav.documentation")}</span>
        </a>
        <a className="stitch-nav-item" href={`mailto:${BRAND.supportEmail}`}>
          <span className="stitch-icon" aria-hidden="true">
            <LifeBuoy size={16} />
          </span>
          <span>{t("stitch.sidenav.support")}</span>
        </a>
      </div>
    </aside>
  );
}

function StitchPageHeader({
  demoMode,
  section,
  t,
  view
}: {
  demoMode: boolean;
  section: NavItem;
  t: Translate;
  view: DashboardView;
}) {
  return (
    <section className="stitch-pageheader-row">
      <div className="stitch-pageheader">
        <span className="stitch-eyebrow">
          {t(section.eyebrowKey)}
          {demoMode && section.showMetrics ? (
            <span className="stitch-demo-pill">{t("stitch.demoBadge")}</span>
          ) : null}
        </span>
        <h1>{t(section.titleKey)}</h1>
      </div>
      {/* The network notice is provider-specific copy ("Provider setup open ·
          Job routing controlled"). It used to render for ANY metrics-bearing
          section, which put provider status text on the partner overview.
          Render it only on the provider console. */}
      {view === "provider" && section.showMetrics ? (
        <div className="stitch-network-notice" role="status">
          <span className="stitch-icon" aria-hidden="true">
            <ShieldCheck size={18} />
          </span>
          <div>
            <span className="label">{t("stitch.page.provider.noticeLabel")}</span>
            <p>{t("stitch.page.provider.notice")}</p>
          </div>
        </div>
      ) : null}
    </section>
  );
}

type Metric = {
  label: string;
  value: string;
  delta?: string;
  tone?: "primary" | "secondary";
  progress?: number;
};

function StitchMetricStrip({
  demoMode,
  t,
  view
}: {
  demoMode: boolean;
  t: Translate;
  view: DashboardView;
}) {
  const metrics = useMemo<Metric[]>(() => {
    // When the user is authenticated but real data hasn't been wired yet,
    // render an em-dash so we never show fake numbers in production.
    const v = (real: string) => (demoMode ? real : "—");
    const d = (real: string) => (demoMode ? real : undefined);
    const p = (real: number) => (demoMode ? real : undefined);

    if (view === "admin") {
      return [
        { label: t("stitch.metric.activeProviders"), value: v("1,284"), delta: d(t("stitch.metric.activeProviders.delta")), tone: "secondary" },
        { label: t("stitch.metric.pendingApps"),    value: v("42"),     delta: d(t("stitch.metric.pendingApps.delta")),    tone: "primary" },
        { label: t("stitch.metric.inFlightJobs"),   value: v("8,419"),  delta: d(t("stitch.metric.inFlightJobs.delta")),   tone: "secondary" },
        { label: t("stitch.metric.auditedWrites"),  value: v("311"),    delta: d(t("stitch.metric.auditedWrites.delta")),  tone: "primary" }
      ];
    }
    if (view === "partner") {
      return [
        { label: t("stitch.metric.netSettlements"),  value: v("£142.9k"), delta: d(t("stitch.metric.netSettlements.delta")), tone: "primary" },
        { label: t("stitch.metric.pendingClearing"), value: v("£18.2k"),  progress: p(65) },
        { label: t("stitch.metric.processingFee"),   value: v("0.42%"),   delta: d(t("stitch.metric.processingFee.delta")), tone: "secondary" },
        { label: t("stitch.metric.disputeRate"),     value: v("0.02%"),   delta: d(t("stitch.metric.disputeRate.delta")),   tone: "secondary" }
      ];
    }
    return [
      { label: t("stitch.metric.openOffers"),    value: v("12"),     delta: d(t("stitch.metric.openOffers.delta")),    tone: "primary",   progress: p(66) },
      { label: t("stitch.metric.activeJobs"),    value: v("43"),     delta: d(t("stitch.metric.activeJobs.delta")) },
      { label: t("stitch.metric.monthlyPayout"), value: v("$14.2k"), delta: d(t("stitch.metric.monthlyPayout.delta")), tone: "secondary" },
      { label: t("stitch.metric.capacityUsed"),  value: v("68%"),    tone: "secondary", progress: p(68) }
    ];
  }, [demoMode, view, t]);

  return (
    <section className="stitch-metric-row" aria-label={t("stitch.page.provider.title")}>
      {metrics.map((metric) => (
        <article
          className={"stitch-metric" + (metric.tone === "secondary" ? " is-secondary" : "")}
          key={metric.label}
        >
          <p className="stitch-label">{metric.label}</p>
          <div className={"stitch-value" + (metric.tone ? " is-" + metric.tone : "")}>
            {metric.value}
          </div>
          {metric.delta ? <span className="stitch-delta">{metric.delta}</span> : null}
          {metric.progress !== undefined ? (
            <div className="stitch-progress" aria-hidden="true">
              <span style={{ width: `${metric.progress}%` }} />
            </div>
          ) : null}
        </article>
      ))}
    </section>
  );
}

function StitchFooter({ t }: { t: Translate }) {
  return (
    <footer className="stitch-footer">
      <div className="legal">
        <strong>{t("stitch.footer.brand")}</strong>
        <span>
          {BRAND.legalName} · {t("footer.companyNo")} {BRAND.companyNumber} · {t("footer.registered")} · {t("footer.rights")}
        </span>
      </div>
      {/* Only link pages that actually exist. The former "API terms" /
          "Security" / "Status" entries were href="#" placeholders and are
          removed until real destinations ship. */}
      <div className="stitch-footer-links">
        <a href="/docs#terms-of-service">{t("footer.terms")}</a>
        <a href="/docs#privacy-policy">{t("footer.privacy")}</a>
        <ConsentSettingsLink t={t} />
      </div>
    </footer>
  );
}

// Partner Platform Batch P3: the partner webhook surface is now backed by
// real partner self-serve endpoints (create / update / delete subscription
// against integration_outbox-driven delivery), so the CONTRACT-002 projection
// of `partnerWebhookEvents` from the shared registry is sourced inside
// PartnerWebhooksPanel directly — keep adding new public event types there,
// not in this file. The Docs section remains placeholder until partner
// capabilities load from real backend state.

function PartnerHubView({
  canChangePassword = false,
  locale,
  onChangePassword,
  onLogout,
  onNavigateSection,
  onReportBug,
  sectionId,
  session,
  t
}: {
  canChangePassword?: boolean;
  locale: SupportedLocale;
  onChangePassword?: () => void;
  onLogout?: () => void;
  onNavigateSection?: (sectionId: string) => void;
  onReportBug?: () => void;
  sectionId: string;
  session: Session | null;
  t: Translate;
}) {
  // Partner Platform Batches P2 + P3: partner self-serve flows wired to
  // real backend state.
  //
  //   * "overview" — application status (with submit form on first
  //     visit) so the user always lands on actionable signal.
  //   * "secrets" — real partner API key panel (mint/rotate/revoke). The
  //     panel renders an "apply first" gate when no approved application
  //     is linked yet.
  //   * "webhooks" — real webhook subscription panel (create/update/delete
  //     against integration_outbox-backed delivery). Same "apply first"
  //     gate as secrets — partners without an approved application can't
  //     subscribe to events they have no scope to consume.
  //   * "docs" — partner docs (still placeholder; gated surface ships
  //     in a later batch).
  const [applicationApproved, setApplicationApproved] = useState(false);
  // RM-17: a FAILED approval lookup (network error, 5xx, expired session) is
  // distinct from a genuine "not approved" result. The old code mapped both
  // to "blocked", so an approved partner whose lookup transiently failed was
  // shown the "apply first" gate. "error" renders a retry panel instead.
  const [approvalLookup, setApprovalLookup] = useState<
    "blocked" | "loading" | "approved" | "error"
  >("blocked");
  const [approvalLookupEpoch, setApprovalLookupEpoch] = useState(0);
  const retryApprovalLookup = useCallback(() => {
    setApprovalLookupEpoch((epoch) => epoch + 1);
  }, []);
  const handleApproved = useCallback(() => {
    setApplicationApproved(true);
    onNavigateSection?.("overview");
  }, [onNavigateSection]);

  useEffect(() => {
    let cancelled = false;
    if (applicationApproved) {
      setApprovalLookup("approved");
      return () => {
        cancelled = true;
      };
    }
    if (!session || accountRoleForSession(session) !== "partner") {
      setApprovalLookup("blocked");
      return () => {
        cancelled = true;
      };
    }

    setApprovalLookup("loading");
    void listOwnPartnerApplications(session)
      .then((response) => {
        if (cancelled) return;
        setApprovalLookup(
          response.applications.some((application) => application.status === "approved")
            ? "approved"
            : "blocked"
        );
      })
      .catch(() => {
        if (!cancelled) setApprovalLookup("error");
      });

    return () => {
      cancelled = true;
    };
  }, [applicationApproved, approvalLookupEpoch, session]);

  const partnerAccessApproved =
    applicationApproved || approvalLookup === "approved";
  const partnerAccessLoading =
    !partnerAccessApproved && approvalLookup === "loading";

  const [partnerOverview, setPartnerOverview] = useState<PartnerSelfOverview | null>(null);
  const currentPartnerOwnerId = session?.user.id ?? null;
  const [partnerOverviewOwnerId, setPartnerOverviewOwnerId] = useState<string | null>(currentPartnerOwnerId);
  const [partnerOverviewError, setPartnerOverviewError] = useState(false);
  const [launchDraftState, setLaunchDraftState] = useState<{ ownerId: string; version: string; state: PartnerLifecycleDraftState } | null>(null);
  const visiblePartnerOverview = partnerOverviewOwnerId === currentPartnerOwnerId ? partnerOverview : null;
  const applicationVersion = visiblePartnerOverview?.productionApplication
    ? `${visiblePartnerOverview.productionApplication.id}:${visiblePartnerOverview.productionApplication.updatedAt}` : "none";
  const visibleLaunchDraftState = launchDraftState?.ownerId === currentPartnerOwnerId && launchDraftState.version === applicationVersion
    ? launchDraftState.state : null;
  const saveLaunchDraftState = useCallback((state: PartnerLifecycleDraftState) => {
    if (currentPartnerOwnerId) setLaunchDraftState({ ownerId: currentPartnerOwnerId, version: applicationVersion, state });
  }, [currentPartnerOwnerId, applicationVersion]);
  const overviewRequest = useRef(0);
  const overviewOwnerId = useRef<string | null>(session?.user.id ?? null);
  const refreshPartnerOverview = useCallback(async () => {
    const request = ++overviewRequest.current;
    const ownerId = session?.user.id ?? null;
    if (overviewOwnerId.current !== ownerId) {
      overviewOwnerId.current = ownerId;
      setPartnerOverview(null);
      setPartnerOverviewOwnerId(null);
      setLaunchDraftState(null);
    }
    if (!session || !partnerAccessApproved) {
      setPartnerOverview(null);
      return;
    }
    setPartnerOverviewError(false);
    try {
      const result = await getPartnerSelfOverview(session);
      if (request === overviewRequest.current) {
        setPartnerOverview(result);
        setPartnerOverviewOwnerId(ownerId);
      }
    } catch {
      if (request === overviewRequest.current) {
        setPartnerOverviewError(true);
      }
    }
  }, [session, partnerAccessApproved]);
  useEffect(() => {
    void refreshPartnerOverview();
    return () => { overviewRequest.current += 1; };
  }, [refreshPartnerOverview, sectionId]);
  const canCreateProductionKeys = visiblePartnerOverview?.partner.accessStage === "production";

  const gatedSection =
    sectionId === "payouts" ||
    sectionId === "secrets" ||
    sectionId === "webhooks" ||
    sectionId === "sandbox" ||
    sectionId === "docs";

  if (partnerAccessLoading && gatedSection) {
    return (
      <article className="panel partner-application-panel">
        <p>{t("partnerApplication.loading")}</p>
      </article>
    );
  }

  // RM-17: lookup failure is an error state, not a locked state. Don't show
  // the "apply first" gate when we simply couldn't read the application.
  if (!partnerAccessApproved && approvalLookup === "error" && gatedSection) {
    return (
      <article className="panel partner-application-panel">
        <div className="form-note error" role="alert">
          <AlertTriangle size={16} />
          <div>
            <strong>{t("partnerHub.lookupFailedTitle")}</strong>
            <span>{t("partnerHub.lookupFailedBody")}</span>
          </div>
        </div>
        <button
          className="secondary-button"
          onClick={retryApprovalLookup}
          type="button"
        >
          <RefreshCcw size={16} />
          <span>{t("partnerHub.lookupRetry")}</span>
        </button>
      </article>
    );
  }

  if (sectionId === "orders") {
    return (
      <PartnerOrdersPanel
        applicationApproved={partnerAccessApproved}
        onApplyClick={() => onNavigateSection?.("overview")}
        session={session}
        locale={locale}
        t={t}
      />
    );
  }
  if (sectionId === "shipping") {
    return (
      <PartnerShippingCoveragePanel
        applicationApproved={partnerAccessApproved}
        onApplyClick={() => onNavigateSection?.("overview")}
        session={session}
        locale={locale}
        t={t}
      />
    );
  }
  if (sectionId === "payouts") {
    return (
      <PartnerPayoutsPanel
        applicationApproved={partnerAccessApproved}
        onApplyClick={() => onNavigateSection?.("overview")}
        onNavigateSection={onNavigateSection}
        session={session}
        locale={locale}
        t={t}
      />
    );
  }
  if (sectionId === "secrets") {
    return (
      <PartnerApiKeysPanel
        applicationApproved={partnerAccessApproved}
        canCreateProductionKeys={canCreateProductionKeys}
        locale={locale}
        onApplyClick={() => onNavigateSection?.("overview")}
        session={session}
        t={t}
      />
    );
  }
  if (sectionId === "webhooks") {
    return (
      <PartnerWebhooksPanel
        applicationApproved={partnerAccessApproved}
        locale={locale}
        onApplyClick={() => onNavigateSection?.("overview")}
        session={session}
        t={t}
      />
    );
  }
  if (sectionId === "sandbox") {
    return (
      <SandboxPlaygroundPanel
        applicationApproved={partnerAccessApproved}
        onApplyClick={() => onNavigateSection?.("overview")}
        session={session}
        t={t}
      />
    );
  }
  if (sectionId === "docs") {
    return (
      <PartnerDocsPanel
        applicationApproved={partnerAccessApproved}
        locale={locale}
        onApplyClick={() => onNavigateSection?.("overview")}
        session={session}
        t={t}
      />
    );
  }
  if (sectionId === "account") {
    return <>
      <AccountSettingsPanel
        canChangePassword={canChangePassword}
        locale={locale}
        onChangePassword={onChangePassword}
        onLogout={onLogout}
        onReportBug={onReportBug}
        session={session}
        t={t}
      />
      {partnerAccessApproved ? <>
        <PartnerMatchingPreferencesPanel session={session} t={t} />
        <PartnerAppCoveragePanel locale={locale} session={session} t={t} />
        <PartnerNotificationPreferencesPanel session={session} t={t} />
      </> : null}
    </>;
  }
  if (partnerAccessApproved && !visiblePartnerOverview) {
    return <article className="panel" aria-busy={!partnerOverviewError}>
      {partnerOverviewError ? <>
        <p className="form-note error" role="alert">{t("partnerHub.lookupFailedBody")}</p>
        <button className="secondary-button" onClick={() => void refreshPartnerOverview()} type="button">{t("partnerHub.lookupRetry")}</button>
      </> : <p>{t("partnerApplication.loading")}</p>}
    </article>;
  }
  return (
    <>
      {partnerOverviewError && visiblePartnerOverview ? <p className="form-note error" role="alert">
        {t("partnerHub.lookupFailedBody")} <button className="secondary-button" onClick={() => void refreshPartnerOverview()} type="button">{t("partnerHub.lookupRetry")}</button>
      </p> : null}
      {visiblePartnerOverview ? <>
        {visiblePartnerOverview.partner.state === "production_active" && visiblePartnerOverview.keyCounts.production > 0 ? <>
          <PartnerOverviewPanel locale={locale} onNavigateSection={onNavigateSection} overview={visiblePartnerOverview} session={session} t={t} />
          <details className="panel partner-intake-settings">
            <summary>{t("partnerOnboarding.complete")}</summary>
            <PartnerOnboardingPanel locale={locale} overview={visiblePartnerOverview} onRefresh={refreshPartnerOverview} onNavigateSection={onNavigateSection} session={session} t={t} launchDraftState={visibleLaunchDraftState} onLaunchDraftStateChange={saveLaunchDraftState} />
          </details>
        </> : <PartnerOnboardingPanel locale={locale} overview={visiblePartnerOverview} onRefresh={refreshPartnerOverview} onNavigateSection={onNavigateSection} session={session} t={t} launchDraftState={visibleLaunchDraftState} onLaunchDraftStateChange={saveLaunchDraftState} />}
      </> : <PartnerApplicationForm onApproved={handleApproved} locale={locale} session={session} t={t} />}
      <DashboardHelpStrip onReportBug={onReportBug} t={t} />
    </>
  );
}

// Partner Platform Batch P2: PartnerSecretsPanel placeholder is gone — the
// real key lifecycle panel (PartnerApiKeysPanel) is wired into
// PartnerHubView directly.
//
// Partner Platform Batch P3: PartnerWebhooksPanel placeholder is also gone —
// the real subscription lifecycle panel ships as ./components/PartnerWebhooksPanel
// and is wired into PartnerHubView directly. PartnerDocsPanel now gives
// partners a copyable Codex/Claude integration brief while the full docs
// surface stays protected behind the partner OpenAPI/docs endpoints.

function PartnerDocsPanel({
  applicationApproved,
  locale,
  onApplyClick,
  session,
  t
}: {
  applicationApproved: boolean;
  locale: SupportedLocale;
  onApplyClick?: () => void;
  session: Session | null;
  t: Translate;
}) {
  return (
    <PartnerApiInstructionsPanel
      applicationApproved={applicationApproved}
      locale={locale}
      onApplyClick={onApplyClick}
      session={session}
      t={t}
    />
  );
}

// Partner Platform Batch P2: the PartnerBanner placeholder is gone — the
// partner overview now renders the real PartnerApplicationForm which has
// its own header + state machine and replaces the announcement surface.
function BrandLogo() {
  // Render the brand lockup as a plain `<img>` of the horizontal SVG. The
  // wordmark inside that SVG is already outlined paths (not `<text>`), so
  // there's no page-font dependency — what ships in the asset is what
  // renders. Sidebar collapsed mode swaps to the mark-only SVG via CSS.
  return (
    <span className="brand-logo">
      <img alt={BRAND.name} className="brand-logo-full" src={BRAND.logos.fullColorHorizontal} />
      <img alt="" aria-hidden="true" className="brand-logo-mark" src={BRAND.logos.fullColorMark} />
    </span>
  );
}
